/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Rider Operations Module — all rider-specific UI components.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { usePersistentState } from '../../hooks/usePersistentState';
import { getRepository } from '../../config/repository';
import { apiFetch } from '../../data/ApiModuleRepository';
import { User, UserRole, Organization, OrgPlan, PlanFeature, PERMISSIONS, Permission, MODULE_IDS, ModuleId, Brand, isPlatformRole } from '../../domain/entities';
import { Rider, RequestType, RequestStatus, WorkflowRequest, RiderHistoryEntry, RiderSettings } from './types';
import { hasPermission as hasPermFn, canAccessModule, hasTabVariant } from '../../core/security';
import { isAtPlanLimit, PLAN_LABELS } from '../../core/planGating';
import { ModuleTabs, TabGate } from '../shared/ModuleTabs';
import {
  Users, UserPlus, Settings as SettingsIcon, Search, Download, Upload,
  Trash2, Edit2, X, Check, AlertCircle, FileText, Plus, ChevronRight,
  Copy, ClipboardCheck, LayoutDashboard, Clock, Briefcase, Eye, Phone,
  Smartphone, ArrowRightLeft, Inbox, BarChart2, ArrowLeft, ShieldCheck,
  ToggleLeft, ToggleRight, Lock, History, Camera, ClipboardList, Shield,
  RotateCcw,
} from 'lucide-react';
import { cn } from '../../ui/utils';
import { hasDataChanged } from '../../core/utils';
import { useToast } from '../../ui/Toast';
import { ConfirmDeleteDialog, type DeleteConfirmState } from '../../ui/ConfirmDeleteDialog';
import { CustomSelect } from '../../components/shared/CustomSelect';
import { flattenBranches, getBrandByBranchId, getBranchById, getBranchesForBrand } from '../../core/brandBranches';
import * as XLSX from 'xlsx-js-style';
import domtoimage from 'dom-to-image-more';

// ---------------------------------------------------------------------------
// Shared helpers (local copies so this module is self-contained)
// ---------------------------------------------------------------------------

function ProtectedRoute({ children, allowedRoles, permission, moduleId, user }: {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  permission?: Permission;
  moduleId?: ModuleId;
  user: User | null;
}) {
  if (!user) return <Navigate to='/' replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to='/403' replace />;
  if (moduleId && !canAccessModule(user, moduleId, permission)) return <Navigate to='/403' replace />;
  else if (!moduleId && permission && !hasPermFn(user, permission)) return <Navigate to='/403' replace />;
  return <>{children}</>;
}

function PermissionGate({ permission, user, children }: {
  permission: Permission;
  user: User | null;
  children: React.ReactNode;
}) {
  if (!hasPermFn(user, permission)) return null;
  return <>{children}</>;
}

function PlanLimitModal({ resource, currentPlan, onClose }: {
  resource: 'Hubs' | 'Users' | 'Riders';
  currentPlan: OrgPlan | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const planLabel = currentPlan ? PLAN_LABELS[currentPlan] : "current";
  const nextLabel = currentPlan === OrgPlan.FREE ? 'Pro' : 'Enterprise';
  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 space-y-5 text-center">
        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto">
          <Lock size={24} className="text-amber-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-zinc-900">Limit Reached</h3>
          <p className="text-sm text-zinc-500 mt-2">
            You've reached the <strong>{resource}</strong> limit for the{' '}
            <strong>{planLabel}</strong> plan. Upgrade to <strong>{nextLabel}</strong> to add more.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold hover:bg-zinc-50">Cancel</button>
          <button onClick={() => { onClose(); navigate("/saas-admin/subscriptions"); }} className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700">Upgrade Plan</button>
        </div>
      </div>
    </div>
  );
}

function ListManager({ title, items, onAdd, onRemove }: any) {
  const [val, setVal] = useState("");
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
      <h3 className="font-bold text-zinc-900">{title}</h3>
      <div className="flex gap-2">
        <input type="text" value={val} onChange={(e) => setVal(e.target.value)}
          className="flex-1 px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 text-sm"
          placeholder={"Add " + title + "..."} />
        <button onClick={() => { onAdd(val); setVal(""); }} className="p-2 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800">
          <Plus size={20} />
        </button>
      </div>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {items.map((item: string, i: number) => (
          <div key={i} className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl group">
            <span className="text-sm font-medium text-zinc-700">{item}</span>
            <button onClick={() => onRemove(i)} className="text-zinc-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

type LegacyBranchRef = {
  branchId?: string | null;
  hubCode?: string | null;
  brandId?: string | null;
};

type LegacyHistoryEntry = RiderHistoryEntry & {
  hubCode?: string | null;
  unit?: string | null;
};

type LegacyRiderRecord = Omit<Rider, 'history'> & {
  hubCode?: string | null;
  unit?: string | null;
  history?: LegacyHistoryEntry[];
};

function getRiderModuleBrands(brands: Brand[]): Brand[] {
  return brands.filter((brand) => brand.enabledModules?.includes('rider-manager'));
}

function resolveBranchId(brands: Brand[], branchIdOrCode?: string | null): string {
  const raw = String(branchIdOrCode ?? '').trim();
  if (!raw) return '';

  const riderBrands = getRiderModuleBrands(brands);
  const branch =
    getBranchById(riderBrands, raw) ??
    flattenBranches(riderBrands).find((candidate) => candidate.code.toLowerCase() === raw.toLowerCase());

  return branch?.id ?? raw;
}

function getBranchMeta(brands: Brand[], source?: LegacyBranchRef | string | null) {
  const raw = typeof source === 'string' ? source : source?.branchId ?? source?.hubCode ?? null;
  const riderBrands = getRiderModuleBrands(brands);
  const branchId = resolveBranchId(riderBrands, raw);
  const branch =
    branchId
      ? getBranchById(riderBrands, branchId) ??
        flattenBranches(riderBrands).find((candidate) => candidate.code.toLowerCase() === branchId.toLowerCase())
      : undefined;
  const brand =
    branch
      ? getBrandByBranchId(riderBrands, branch.id)
      : typeof source === 'object' && source?.brandId
      ? riderBrands.find((candidate) => candidate.id === source.brandId)
      : undefined;

  const fallback = String(raw ?? '').trim();
  const branchName = (branch?.name ?? fallback) || 'Unassigned branch';
  const branchCode = (branch?.code ?? fallback) || '—';
  const brandName = brand?.name ?? 'Unassigned brand';

  return {
    branch,
    brand,
    branchId: branch?.id ?? branchId,
    branchName,
    branchCode,
    brandName,
    fullLabel: branch ? `${brandName} — ${branch.name}` : branchName,
    compactLabel: branch ? `${branch.name} (${branch.code})` : branchName,
  };
}

function getScopedBranches(brands: Brand[], user: User | null) {
  const riderBrands = getRiderModuleBrands(brands);
  const branches = flattenBranches(riderBrands);

  if (!user || isPlatformRole(user.role)) {
    return branches;
  }

  const assignedBrands = user.assignedBrands ?? [];
  const assignedBranches = user.assignedBranches ?? [];

  if (assignedBrands.length === 0 && assignedBranches.length === 0) {
    return branches;
  }

  return branches.filter(
    (branch) => assignedBrands.includes(branch.brandId) || assignedBranches.includes(branch.id),
  );
}

function getScopedBrands(brands: Brand[], user: User | null) {
  const visibleBranchIds = new Set(getScopedBranches(brands, user).map((branch) => branch.id));
  return getRiderModuleBrands(brands).filter((brand) =>
    (brand.branches ?? []).some((branch) => visibleBranchIds.has(branch.id)),
  );
}

function getHistoryEntries(rider: LegacyRiderRecord, brands: Brand[]): RiderHistoryEntry[] {
  if (rider.history?.length) {
    return rider.history
      .map((entry) => {
        const branchId = resolveBranchId(brands, entry.branchId ?? entry.hubCode);
        return branchId ? { ...entry, branchId } : null;
      })
      .filter((entry): entry is RiderHistoryEntry => entry !== null);
  }

  const branchId = resolveBranchId(brands, rider.branchId ?? rider.hubCode);
  return branchId
    ? [{ branchId, startDate: rider.createdAt, endDate: null, daysSpent: null }]
    : [];
}

function RiderOperationsModule({ 
  user, 
  riders, 
  allRiders,
  requests, 
  settings, 
  deleteRider, 
  toggleRiderStatus, 
  isAdmin, 
  submitRequest,
  updateRequestStatus,
  addRider,
  updateRider,
  checkFileNumberConflict,
  setShowDetailPopup,
  updateSettings
}: any) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const isEmployee = user?.role === UserRole.EMPLOYEE;
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/riders' },
    { id: 'database', label: 'Database', icon: Users, path: '/riders/database' },
    ...(!isEmployee ? [{ 
      id: 'operations', 
      label: isAdmin ? 'Request Inbox' : 'Submit Request', 
      icon: isAdmin ? Inbox : Plus, 
      path: isAdmin ? '/riders/requests' : '/riders/submit' 
    }] : []),
    { id: 'analytics', label: 'Analytics', icon: BarChart2, path: '/riders/analytics' },
    ...(isAdmin ? [{ id: 'settings', label: 'App Settings', icon: SettingsIcon, path: '/riders/settings' }] : [])
  ];

  const activeTab = tabs.find(t => location.pathname === t.path) || tabs[0];

  return (
    <div className="space-y-8">
      {/* Module Header & Tabs */}
      <div className="bg-white border border-zinc-200 rounded-xl p-2 shadow-sm flex items-center gap-2 w-fit mx-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-200",
              location.pathname === tab.path 
                ? "bg-zinc-900 text-white shadow-lg shadow-zinc-900/20" 
                : "text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900"
            )}
          >
            <tab.icon size={18} />
            {tab.label}
            {tab.id === 'operations' && isAdmin && requests.filter((r: any) => r.status === RequestStatus.PENDING).length > 0 && (
              <span className="ml-1 w-5 h-5 bg-emerald-500 text-white text-[10px] flex items-center justify-center rounded-full">
                {requests.filter((r: any) => r.status === RequestStatus.PENDING).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        <Routes>
          <Route index element={<RiderDashboard riders={riders} settings={settings} user={user} />} />
          <Route path="database" element={<RidersList riders={riders} settings={settings} deleteRider={deleteRider} toggleRiderStatus={toggleRiderStatus} submitRequest={submitRequest} setSelectedRider={setShowDetailPopup} user={user} addRider={addRider} onEditRider={() => {}} />} />
          <Route path="requests" element={<RequestInbox requests={requests} updateRequestStatus={updateRequestStatus} addRider={addRider} settings={settings} setSelectedRider={setShowDetailPopup} riders={allRiders} user={user} />} />
          <Route path="submit" element={<SubmitRequestForm submitRequest={submitRequest} settings={settings} />} />
          <Route path="analytics" element={<GlobalAnalytics riders={riders} settings={settings} user={user} />} />
          <Route path="settings" element={
            <RiderSettingsPage settings={settings} updateSettings={updateSettings} />
          } />
        </Routes>
      </div>
    </div>
  );
}

// ─── Rider Settings Page (Hub Mapping + Numbering Engine) ────────────────────
function RiderSettingsPage({ settings, updateSettings }: { settings: RiderSettings; updateSettings: (s: RiderSettings) => void }) {
  return <RiderAppSettingsPage settings={settings} updateSettings={updateSettings} />;
}

function RequestInbox({ requests, updateRequestStatus, addRider, updateRider, settings, setSelectedRider, riders, user }: any) {
  const [selectedRequest, setSelectedRequest] = useState<WorkflowRequest | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const toast = useToast();

  useEffect(() => {
    getRepository().getBrands().then((all) => setBrands(getRiderModuleBrands(all)));
  }, []);

  const getRiderForRequest = (req: WorkflowRequest) => {
    if (req.riderId) {
      return riders.find((r: any) => r.id === req.riderId);
    }
    return null;
  };

  const getRequestBranchLabel = (req: WorkflowRequest) => getBranchMeta(brands, req.branchId).fullLabel;

  const handleApprove = async (req: WorkflowRequest) => {
    const targetBranchId = resolveBranchId(brands, req.branchId);
    if (!targetBranchId) {
      toast.error('Target branch missing.', 'Select a valid branch before approving this request.');
      return;
    }

    const targetBranchMeta = getBranchMeta(brands, targetBranchId);
    let riderId = req.riderId;
    if (req.type === RequestType.NEW_RIDER && req.riderData) {
      const newRider = await addRider({
        ...req.riderData,
        branchId: targetBranchId,
        brandId: req.riderData.brandId ?? targetBranchMeta.brand?.id,
      });
      riderId = newRider.id;
    } else if (req.type === RequestType.TRANSFER && req.riderId) {
      const rider = riders.find((r: any) => r.id === req.riderId) as LegacyRiderRecord | undefined;
      if (rider) {
        const now = Date.now();
        const currentHistory = getHistoryEntries(rider, brands);

        const updatedHistory: RiderHistoryEntry[] = currentHistory.map((entry, idx) => {
          if (idx === currentHistory.length - 1 && entry.endDate === null) {
            return {
              ...entry,
              endDate: now,
              daysSpent: Math.max(1, Math.ceil((now - entry.startDate) / (1000 * 60 * 60 * 24)))
            };
          }
          return entry;
        });
        updatedHistory.push({ branchId: targetBranchId, startDate: now, endDate: null, daysSpent: null });

        await updateRider({
          ...rider,
          branchId: targetBranchId,
          brandId: targetBranchMeta.brand?.id ?? rider.brandId,
          history: updatedHistory,
        });
      }
    }
    await updateRequestStatus(req.id, RequestStatus.APPROVED, adminNote, riderId);

    // Fire webhook if configured
    const apiSettings = await getRepository().getApiSettings();
    if (apiSettings.webhookEnabled && apiSettings.webhookUrl) {
      const payload = {
        event: 'rider.approved',
        timestamp: Date.now(),
        data: riderId ? riders.find((r: any) => r.id === riderId) : req.riderData
      };
      fetch(apiSettings.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});
    }

    // Audit log — approval
    if (user) {
      const riderLabel = req.riderData?.name || (riderId ? riders.find((r: any) => r.id === riderId)?.name : '') || 'Unknown';
      await getRepository().saveSystemLog({
        id: crypto.randomUUID(),
        userId: user.id,
        userName: user.name,
        action: `Approved ${req.type === RequestType.NEW_RIDER ? 'new rider' : 'transfer'} request for "${riderLabel}"`,
        riderId: riderId || undefined,
        riderName: riderLabel,
        timestamp: Date.now(),
        moduleId: 'rider-ops',
      });
    }

    setSelectedRequest(null);
    setAdminNote('');
    toast.success('Rider Approved!', `${req.type === RequestType.NEW_RIDER ? 'New rider' : 'Transfer'} request for "${req.riderData?.name || getRiderForRequest(req)?.name || 'rider'}" has been approved.`);
  };

  const handleReject = async (req: WorkflowRequest) => {
    await updateRequestStatus(req.id, RequestStatus.REJECTED, adminNote);

    // Audit log — rejection
    if (user) {
      const riderLabel = req.riderData?.name || 'Unknown';
      await getRepository().saveSystemLog({
        id: crypto.randomUUID(),
        userId: user.id,
        userName: user.name,
        action: `Rejected ${req.type.replace(/_/g, ' ').toLowerCase()} request for "${riderLabel}"`,
        riderName: riderLabel,
        timestamp: Date.now(),
        moduleId: 'rider-ops',
      });
    }

    setSelectedRequest(null);
    setAdminNote('');
    toast.error('Request Rejected', `The ${req.type.replace(/_/g, ' ').toLowerCase()} request for "${req.riderData?.name || 'rider'}" has been rejected.`);
  };

  const pendingRequests = requests.filter((r: any) => r.status === RequestStatus.PENDING);
  const historyRequests = requests.filter((r: any) => r.status !== RequestStatus.PENDING);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Operations Inbox</h2>
        <p className="text-sm text-slate-500 mt-1">Review and approve operational requests from Store Managers.</p>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          <h3 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Clock size={20} /> Pending Requests
          </h3>
          <div className="space-y-4">
            {pendingRequests.length === 0 ? (
              <div className="p-12 text-center bg-white border border-dashed border-zinc-200 rounded-xl text-slate-400 italic">
                No pending requests at this time.
              </div>
            ) : (
              pendingRequests.map((req: any) => (
                <div key={req.id} className="bg-white border border-zinc-200 p-4 rounded-xl shadow-sm flex items-center justify-between group">
                  <div className="flex items-center gap-6">
                    <div className={cn(
                      "w-12 h-12 rounded-2xl flex items-center justify-center text-white",
                      req.type === RequestType.NEW_RIDER ? "bg-emerald-500" : "bg-indigo-500"
                    )}>
                      {req.type === RequestType.NEW_RIDER ? <UserPlus size={24} /> : <ArrowRightLeft size={24} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-zinc-400 uppercase tracking-widest">{req.type.replace('_', ' ')}</span>
                        <span className="w-1 h-1 bg-zinc-300 rounded-full" />
                        <span className="text-xs font-bold text-zinc-900">{req.requesterName}</span>
                      </div>
                      <h4 className="text-lg font-bold text-zinc-900 mt-1">
                        {req.type === RequestType.NEW_RIDER ? (
                          `Register: ${req.riderData?.name}`
                        ) : (
                          <button 
                            onClick={() => {
                              const rider = getRiderForRequest(req);
                              if (rider) setSelectedRider(rider);
                            }}
                            className="hover:underline text-left"
                          >
                            Transfer: {getRiderForRequest(req)?.name || 'Unknown Rider'}
                          </button>
                        )}
                      </h4>
                      <p className="text-sm text-zinc-500">Target Branch: <span className="font-bold text-zinc-900">{getRequestBranchLabel(req)}</span></p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedRequest(req)}
                    className="px-6 py-3 bg-zinc-900 text-white rounded-xl font-bold text-sm hover:bg-zinc-800 transition-all"
                  >
                    Review Request
                  </button>
                </div>
              ))
            )}
          </div>

          <h3 className="text-xl font-bold tracking-tight flex items-center gap-2 pt-6">
            <Inbox size={20} /> Request History
          </h3>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b border-slate-200">
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Type</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rider</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Requester</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                </tr>
              </thead>
              <tbody>
                {historyRequests.map((req: any) => (
                  <tr key={req.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors even:bg-slate-50/40">
                    <td className="px-4 py-3 text-xs text-zinc-500">{new Date(req.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-xs font-bold text-zinc-900">{req.type.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-xs font-bold text-zinc-900">
                      <button 
                        onClick={() => {
                          const rider = getRiderForRequest(req);
                          if (rider) setSelectedRider(rider);
                        }}
                        className="hover:underline text-left"
                      >
                        {getRiderForRequest(req)?.name || req.riderData?.name || '—'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{req.requesterName}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest",
                        req.status === RequestStatus.APPROVED ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      )}>
                        {req.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-zinc-900 text-white p-6 rounded-xl shadow-xl">
            <h3 className="text-lg font-bold mb-3">Inbox Stats</h3>
            <div className="space-y-6">
              <div>
                <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest mb-1">Pending</p>
                <p className="text-2xl font-bold">{pendingRequests.length}</p>
              </div>
              <div className="h-px bg-zinc-800" />
              <div>
                <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest mb-1">Total Processed</p>
                <p className="text-2xl font-bold">{historyRequests.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedRequest && (
        <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-[2.5rem] w-full max-w-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-10 space-y-8">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold tracking-tight">Review Request</h3>
                <button onClick={() => setSelectedRequest(null)} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="p-6 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Request Details</p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-zinc-600">Type: <span className="text-zinc-900 font-bold">{selectedRequest.type}</span></p>
                    <p className="text-sm font-medium text-zinc-600">Requester: <span className="text-zinc-900 font-bold">{selectedRequest.requesterName}</span></p>
                    <p className="text-sm font-medium text-zinc-600">Target Branch: <span className="text-zinc-900 font-bold">{getRequestBranchLabel(selectedRequest)}</span></p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Admin Note (Optional)</label>
                  <textarea 
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 transition-all h-24 resize-none"
                    placeholder="Add a reason for approval or rejection..."
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button 
                  onClick={() => handleReject(selectedRequest)}
                  className="flex-1 py-4 bg-white border border-red-200 text-red-600 rounded-2xl font-bold hover:bg-red-50 transition-all"
                >
                  Reject Request
                </button>
                <button 
                  onClick={() => handleApprove(selectedRequest)}
                  className="flex-1 py-4 bg-emerald-500 text-white rounded-2xl font-bold hover:bg-emerald-600 transition-all"
                >
                  Approve & Finalize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmitRequestForm({ user, submitRequest, settings }: any) {
  const navigate = useNavigate();
  const toast = useToast();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    contract: settings.contracts[0] || '',
    brandId: '',
    branchId: '',
  });

  useEffect(() => {
    getRepository().getBrands().then(all => {
      setBrands(getRiderModuleBrands(all));
    });
  }, []);

  const scopedBrands = getScopedBrands(brands, user);
  const availableBranches = formData.brandId
    ? getBranchesForBrand(scopedBrands, formData.brandId)
    : getScopedBranches(brands, user);
  const selectedBranchMeta = getBranchMeta(brands, formData.branchId);

  useEffect(() => {
    if (formData.branchId || availableBranches.length !== 1) return;
    const [onlyBranch] = availableBranches;
    setFormData((prev) => ({
      ...prev,
      branchId: onlyBranch.id,
      brandId: prev.brandId || onlyBranch.brandId,
    }));
  }, [availableBranches, formData.branchId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const branchId = resolveBranchId(brands, formData.branchId);
    const branchMeta = getBranchMeta(brands, branchId);
    if (!branchMeta.branch) {
      toast.error('Branch required.', 'Select the target branch for this rider request.');
      return;
    }

    await submitRequest({
      type: RequestType.NEW_RIDER,
      branchId: branchMeta.branch.id,
      riderData: {
        name: formData.name,
        mobile: formData.mobile,
        contract: formData.contract,
        branchId: branchMeta.branch.id,
        brandId: branchMeta.brand?.id,
      }
    });
    toast.success('Request Submitted!', `New rider request for "${formData.name}" submitted successfully.`);
    navigate('/apps/rider-manager/transfers');
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">Submit Rider Request</h2>
        <p className="text-sm text-slate-500">Request a new rider registration for your assigned brands and branches.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-6 rounded-xl shadow-sm space-y-8">
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Rider Full Name</label>
            <div className="relative">
              <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                onBlur={(e) => setFormData(prev => ({ ...prev, name: toTitleCase(e.target.value) }))}
                className="w-full pl-12 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 transition-all"
                placeholder="e.g. John Doe"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Mobile Number</label>
            <div className="relative">
              <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <input 
                type="tel" 
                required
                value={formData.mobile}
                onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                className="w-full pl-12 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 transition-all"
                placeholder="e.g. 0123456789"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Preferred Contract</label>
              <CustomSelect
                value={formData.contract}
                onChange={v => setFormData(prev => ({ ...prev, contract: v }))}
                options={settings.contracts.map((c: string) => ({ value: c, label: c }))}
                placeholder="Select contract…"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Brand</label>
              <CustomSelect
                value={formData.brandId}
                onChange={v => setFormData(prev => ({ ...prev, brandId: v, branchId: '' }))}
                options={[{ value: '', label: 'All Rider Brands' }, ...scopedBrands.map(b => ({ value: b.id, label: b.name }))]}
                placeholder="Select brand…"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Branch</label>
              <CustomSelect
                value={formData.branchId}
                onChange={v => setFormData(prev => ({ ...prev, branchId: v, brandId: prev.brandId || getBranchMeta(brands, v).brand?.id || '' }))}
                options={availableBranches.map((branch) => ({
                  value: branch.id,
                  label: `${getBrandByBranchId(brands, branch.id)?.name ?? 'Unknown brand'} — ${branch.name} (${branch.code})`,
                }))}
                placeholder="Select branch…"
              />
              <p className="text-[10px] text-emerald-600 font-medium">
                {selectedBranchMeta.branch
                  ? `Branch: ${selectedBranchMeta.compactLabel}`
                  : 'This request needs a branch assignment.'}
              </p>
            </div>
          </div>
        </div>

        <button 
          type="submit"
          disabled={availableBranches.length === 0}
          className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all flex items-center justify-center gap-2"
        >
          Submit Request <ChevronRight size={20} />
        </button>
      </form>
    </div>
  );
}

// ─── Rider Dashboard ─────────────────────────────────────────────────────────
function RiderDashboard({ riders, settings, user }: { riders: Rider[], settings: any, user: User | null }) {
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    getRepository().getBrands().then((all) => setBrands(getRiderModuleBrands(all)));
  }, []);

  const totalRiders = riders.length;
  const activeRiders = riders.filter(r => r.isActive).length;
  const inactiveRiders = totalRiders - activeRiders;
  const activeRate = totalRiders > 0 ? Math.round((activeRiders / totalRiders) * 100) : 0;
  const totalContracts = new Set(riders.map(r => r.contract)).size;
  const totalBranches = new Set(riders.map(r => getBranchMeta(brands, r as LegacyRiderRecord).branchId).filter(Boolean)).size;
  const avgTenure = totalRiders > 0 ? Math.round(riders.reduce((s, r) => s + Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)), 0) / totalRiders) : 0;
  const totalTransfers = riders.reduce((s, r) => s + (r.history?.length ?? 0), 0);

  const contractCounts = riders.reduce((acc, r) => { acc[r.contract] = (acc[r.contract] || 0) + 1; return acc; }, {} as Record<string, number>);
  const branchData = riders.reduce((acc, r) => {
    const branchMeta = getBranchMeta(brands, r as LegacyRiderRecord);
    const key = branchMeta.branchId || branchMeta.branchCode;
    if (!key) return acc;
    if (!acc[key]) {
      acc[key] = {
        branchName: branchMeta.branchName,
        branchCode: branchMeta.branchCode,
        brandName: branchMeta.brandName,
        total: 0,
        active: 0,
      };
    }
    acc[key].total++;
    if (r.isActive) acc[key].active++;
    return acc;
  }, {} as Record<string, { branchName: string; branchCode: string; brandName: string; total: number; active: number }>);
  const topBranches = Object.values(branchData).sort((a, b) => b.total - a.total).slice(0, 6);

  // Tenure distribution
  const tenureBuckets = [
    { label: '0-30d', min: 0, max: 30 },
    { label: '31-90d', min: 31, max: 90 },
    { label: '91-180d', min: 91, max: 180 },
    { label: '180d+', min: 181, max: Infinity },
  ].map(b => ({ ...b, count: riders.filter(r => { const t = Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)); return t >= b.min && t <= b.max; }).length }));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Dashboard</h2>
        <p className="text-sm text-zinc-500 mt-1">Rider operations overview · Real-time status</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-6 gap-3">
        {[
          { label: 'Total Riders', value: String(totalRiders), Icon: Users, bg: 'bg-zinc-100', ib: 'text-zinc-600', tc: 'text-zinc-900' },
          { label: 'Active', value: String(activeRiders), Icon: Check, bg: 'bg-emerald-50', ib: 'text-emerald-600', tc: 'text-emerald-600' },
          { label: 'Inactive', value: String(inactiveRiders), Icon: X, bg: 'bg-red-50', ib: 'text-red-600', tc: 'text-red-600' },
          { label: 'Contracts', value: String(totalContracts), Icon: Briefcase, bg: 'bg-indigo-50', ib: 'text-indigo-600', tc: 'text-indigo-600' },
          { label: 'Branches', value: String(totalBranches), Icon: BarChart2, bg: 'bg-blue-50', ib: 'text-blue-600', tc: 'text-blue-600' },
          { label: 'Avg Tenure', value: `${avgTenure}d`, Icon: Clock, bg: 'bg-amber-50', ib: 'text-amber-600', tc: 'text-amber-600' },
        ].map(c => (
          <div key={c.label} className="bg-white border border-zinc-200 p-4 rounded-xl shadow-sm">
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center mb-2", c.bg, c.ib)}><c.Icon size={16} /></div>
            <div className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-0.5">{c.label}</div>
            <div className={cn("text-xl font-bold tracking-tight", c.tc)}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Active Rate Bar */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Active Rate</span>
          <span className="text-sm font-bold text-zinc-900">{activeRate}%</span>
        </div>
        <div className="w-full h-2.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className={cn("h-full rounded-full", activeRate >= 80 ? "bg-emerald-500" : activeRate >= 50 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${activeRate}%` }} />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[9px] text-emerald-600 font-bold">{activeRiders} active</span>
          <span className="text-[9px] text-red-500 font-bold">{inactiveRiders} inactive</span>
        </div>
      </div>

      {/* Contract Distribution + Branch Overview */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2 mb-3"><Briefcase size={14} className="text-indigo-500" /> Contracts</h3>
          <div className="space-y-2">
            {Object.entries(contractCounts).sort(([,a],[,b]) => b - a).map(([contract, count]) => (
              <div key={contract} className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2">
                <span className="text-[11px] font-semibold text-zinc-700">{contract}</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-zinc-200 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${totalRiders > 0 ? (count / totalRiders) * 100 : 0}%` }} /></div>
                  <span className="text-[11px] font-bold text-indigo-600 w-6 text-right">{count}</span>
                </div>
              </div>
            ))}
            {totalContracts === 0 && <p className="text-xs text-zinc-400 italic text-center py-4">No contracts</p>}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2 mb-3"><BarChart2 size={14} className="text-blue-500" /> Top Branches</h3>
          <div className="space-y-2">
            {topBranches.map((branch) => (
              <div key={`${branch.branchCode}-${branch.branchName}`} className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2">
                <div>
                  <span className="text-[11px] font-bold text-zinc-700">{branch.branchName}</span>
                  <span className="text-[9px] text-zinc-400 ml-1.5">{branch.branchCode} · {branch.brandName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-emerald-600 font-bold">{branch.active}</span>
                  <span className="text-[10px] text-zinc-400">/</span>
                  <span className="text-[11px] font-bold text-zinc-900">{branch.total}</span>
                </div>
              </div>
            ))}
            {topBranches.length === 0 && <p className="text-xs text-zinc-400 italic text-center py-4">No branch data</p>}
          </div>
        </div>
      </div>

      {/* Tenure Distribution */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2 mb-3"><Clock size={14} className="text-amber-500" /> Tenure Distribution</h3>
        <div className="grid grid-cols-4 gap-3">
          {tenureBuckets.map(b => (
            <div key={b.label} className="bg-zinc-50 rounded-lg p-3 text-center">
              <div className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-1">{b.label}</div>
              <div className="text-lg font-bold text-zinc-900">{b.count}</div>
              <div className="text-[9px] text-zinc-400">{totalRiders > 0 ? Math.round((b.count / totalRiders) * 100) : 0}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-4 text-[10px] text-zinc-400">
        <span>Transfers: <span className="text-zinc-600 font-bold">{totalTransfers}</span></span><span>·</span>
        <span>Updated: <span className="text-zinc-600 font-bold">{new Date().toLocaleString()}</span></span>
      </div>
    </div>
  );
}

// ─── Analytics ────────────────────────────────────────────────────────────────
function GlobalAnalytics({ riders, settings, user }: { riders: Rider[], settings: any, user: User | null }) {
  const [brands, setBrands] = useState<Brand[]>([]);
  useEffect(() => { getRepository().getBrands().then(all => setBrands(getRiderModuleBrands(all))); }, []);
  const isAdmin = hasTabVariant(user, 'rider-manager', 'analytics', 'admin');
  const navigate = useNavigate();
  const [analyticsTab, setAnalyticsTab] = usePersistentState<'overview' | 'bi-export'>('view', 'overview');
  
  const totalRiders = riders.length;
  const activeRiders = riders.filter(r => r.isActive).length;
  const uniqueContracts = Array.from(new Set(riders.map(r => r.contract)));
  const totalContracts = uniqueContracts.length;
  
  const contractCounts = riders.reduce((acc, r) => {
    acc[r.contract] = (acc[r.contract] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // ─── Excel Report Downloaders ──────────────────────────────────────────────
  const _pad = settings?.fileNoPadding || 4;
  const _hs: any = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '1E293B' } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: '475569' } } } };
  const _sh = (ws: any, n: number) => { for (let i = 0; i < n; i++) { const c = ws[XLSX.utils.encode_cell({ r: 0, c: i })]; if (c) c.s = _hs; } };
  const _aw = (ws: any, h: string[], rows: any[][]) => { ws['!cols'] = h.map((hd, i) => ({ wch: Math.max(hd.length + 2, ...rows.slice(0, 100).map(r => String(r[i] ?? '').length + 2)) })); };
  const _ds = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');

  const downloadRiderMasterXlsx = () => {
    const wb = XLSX.utils.book_new();
    const h = ['File #', 'Name', 'Email', 'Phone', 'Mobile', 'Branch', 'Branch Code', 'Contract', 'Status', 'Tenure (Days)', 'Transfers', 'Created At'];
    const rows = riders.map(r => {
      const bm = getBranchMeta(brands, r as LegacyRiderRecord);
      return [
        (r.fileNumber || 0).toString().padStart(_pad, '0'), r.name, r.email ?? '', r.phone ?? '', r.mobile,
        bm.branchName, bm.branchCode, r.contract, r.isActive ? 'Active' : 'Inactive',
        Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)), r.history?.length ?? 0,
        new Date(r.createdAt).toLocaleDateString()
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([h, ...rows]); _sh(ws, h.length); _aw(ws, h, rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Rider Master');
    XLSX.writeFile(wb, `Rider_Master_${_ds()}.xlsx`);
  };

  const downloadContractXlsx = () => {
    const wb = XLSX.utils.book_new();
    const cMap = new Map<string, { total: number; active: number; inactive: number; branches: Set<string>; tenure: number[] }>();
    riders.forEach(r => {
      const bm = getBranchMeta(brands, r as LegacyRiderRecord);
      const e = cMap.get(r.contract) ?? { total: 0, active: 0, inactive: 0, branches: new Set<string>(), tenure: [] };
      e.total++; if (r.isActive) e.active++; else e.inactive++;
      e.branches.add(bm.branchCode); e.tenure.push(Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)));
      cMap.set(r.contract, e);
    });
    const h = ['Contract', 'Total', 'Active', 'Inactive', 'Active Rate %', 'Branches', 'Avg Tenure (Days)'];
    const rows = Array.from(cMap.entries()).sort(([, a], [, b]) => b.total - a.total).map(([name, d]) => [
      name, d.total, d.active, d.inactive, d.total > 0 ? Math.round((d.active / d.total) * 100) : 0,
      d.branches.size, d.tenure.length > 0 ? Math.round(d.tenure.reduce((s, v) => s + v, 0) / d.tenure.length) : 0
    ]);
    const ws = XLSX.utils.aoa_to_sheet([h, ...rows]); _sh(ws, h.length); _aw(ws, h, rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Contract Summary');
    XLSX.writeFile(wb, `Contract_Report_${_ds()}.xlsx`);
  };

  const downloadBranchXlsx = () => {
    const wb = XLSX.utils.book_new();
    const bMap = new Map<string, { branchName: string; total: number; active: number; inactive: number; contracts: Set<string>; tenure: number[] }>();
    riders.forEach(r => {
      const bm = getBranchMeta(brands, r as LegacyRiderRecord);
      const key = bm.branchCode;
      const e = bMap.get(key) ?? { branchName: bm.branchName, total: 0, active: 0, inactive: 0, contracts: new Set<string>(), tenure: [] };
      e.total++; if (r.isActive) e.active++; else e.inactive++;
      e.contracts.add(r.contract); e.tenure.push(Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)));
      bMap.set(key, e);
    });
    const h = ['Branch Code', 'Branch', 'Total', 'Active', 'Inactive', 'Active Rate %', 'Contracts', 'Avg Tenure (Days)'];
    const rows = Array.from(bMap.entries()).sort(([, a], [, b]) => b.total - a.total).map(([code, d]) => [
      code, d.branchName, d.total, d.active, d.inactive, d.total > 0 ? Math.round((d.active / d.total) * 100) : 0,
      d.contracts.size, d.tenure.length > 0 ? Math.round(d.tenure.reduce((s, v) => s + v, 0) / d.tenure.length) : 0
    ]);
    const ws = XLSX.utils.aoa_to_sheet([h, ...rows]); _sh(ws, h.length); _aw(ws, h, rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Branch Distribution');
    XLSX.writeFile(wb, `Branch_Report_${_ds()}.xlsx`);
  };

  const downloadStatusTenureXlsx = () => {
    const wb = XLSX.utils.book_new();
    // Sheet 1: Status by Contract
    const cMap2 = new Map<string, { active: number; inactive: number }>();
    riders.forEach(r => { const e = cMap2.get(r.contract) ?? { active: 0, inactive: 0 }; if (r.isActive) e.active++; else e.inactive++; cMap2.set(r.contract, e); });
    const h1 = ['Contract', 'Active', 'Inactive', 'Total', 'Active Rate %'];
    const r1 = Array.from(cMap2.entries()).map(([c, d]) => [c, d.active, d.inactive, d.active + d.inactive, Math.round((d.active / (d.active + d.inactive)) * 100)]);
    r1.push(['TOTAL', activeRiders, totalRiders - activeRiders, totalRiders, totalRiders > 0 ? Math.round((activeRiders / totalRiders) * 100) : 0]);
    const ws1 = XLSX.utils.aoa_to_sheet([h1, ...r1]); _sh(ws1, h1.length); _aw(ws1, h1, r1);
    XLSX.utils.book_append_sheet(wb, ws1, 'Status by Contract');
    // Sheet 2: Tenure Ranges
    const ranges = [
      { label: '0-30 days', min: 0, max: 30 }, { label: '31-90 days', min: 31, max: 90 },
      { label: '91-180 days', min: 91, max: 180 }, { label: '181-365 days', min: 181, max: 365 },
      { label: '365+ days', min: 366, max: Infinity },
    ];
    const h2 = ['Tenure Range', 'Count', 'Active', 'Inactive', '% of Total'];
    const r2 = ranges.map(rng => {
      const m = riders.filter(r => { const t = Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)); return t >= rng.min && t <= rng.max; });
      return [rng.label, m.length, m.filter(r => r.isActive).length, m.filter(r => !r.isActive).length, totalRiders > 0 ? Math.round((m.length / totalRiders) * 100) : 0];
    });
    const ws2 = XLSX.utils.aoa_to_sheet([h2, ...r2]); _sh(ws2, h2.length); _aw(ws2, h2, r2);
    XLSX.utils.book_append_sheet(wb, ws2, 'Tenure Ranges');
    XLSX.writeFile(wb, `Status_Tenure_Report_${_ds()}.xlsx`);
  };

  return (
    <div className="space-y-4 bg-[#121212] rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-white">System Analytics</h2>
          <p className="text-sm text-zinc-400 mt-1">Real-time data and contract distribution · <span className="text-blue-400 font-semibold">{totalRiders}</span> riders · <span className="text-emerald-400 font-semibold">{activeRiders}</span> active · <span className="text-indigo-400 font-semibold">{totalContracts}</span> contracts</p>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl px-4 py-3 flex items-center gap-3 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
        <div className="flex items-center gap-1 bg-[#161616] rounded-lg p-0.5">
          {([
            { key: 'overview' as const, label: 'Overview', Icon: BarChart2 },
            { key: 'bi-export' as const, label: 'BI Export', Icon: FileText },
          ]).map(t => (
            <button key={t.key} onClick={() => setAnalyticsTab(t.key)}
              className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
                analyticsTab === t.key ? "bg-[#2D2D2D] text-blue-400 border-2 border-[#3B82F6] shadow-[0_0_12px_rgba(59,130,246,0.3)]" : "text-zinc-400 hover:text-zinc-200"
              )}>
              <t.Icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {analyticsTab === 'overview' && (
        <>
          {/* Downloadable Reports */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Download size={18} className="text-blue-400" /> Downloadable Reports
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><Users size={14} className="text-zinc-400" /><span className="text-xs font-bold text-white">Rider Master Report</span></div>
                    <p className="text-[10px] text-zinc-500">Complete rider list with details, tenure & transfers</p>
                  </div>
                  <button onClick={downloadRiderMasterXlsx} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold rounded-lg transition-colors shrink-0 ml-3">
                    <Download size={10} /> Excel
                  </button>
                </div>
              </div>
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><Briefcase size={14} className="text-indigo-400" /><span className="text-xs font-bold text-white">Contract Summary</span></div>
                    <p className="text-[10px] text-zinc-500">Contract-level aggregation with active rates & hubs</p>
                  </div>
                  <button onClick={downloadContractXlsx} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold rounded-lg transition-colors shrink-0 ml-3">
                    <Download size={10} /> Excel
                  </button>
                </div>
              </div>
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><BarChart2 size={14} className="text-blue-400" /><span className="text-xs font-bold text-white">Branch Distribution</span></div>
                    <p className="text-[10px] text-zinc-500">Branch-level breakdown with staffing & contract coverage</p>
                  </div>
                  <button onClick={downloadBranchXlsx} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold rounded-lg transition-colors shrink-0 ml-3">
                    <Download size={10} /> Excel
                  </button>
                </div>
              </div>
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><ClipboardList size={14} className="text-amber-400" /><span className="text-xs font-bold text-white">Status & Tenure</span></div>
                    <p className="text-[10px] text-zinc-500">Active/inactive breakdown & tenure range distribution</p>
                  </div>
                  <button onClick={downloadStatusTenureXlsx} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold rounded-lg transition-colors shrink-0 ml-3">
                    <Download size={10} /> Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {analyticsTab === 'bi-export' && (() => {
        const pad = settings?.fileNoPadding || 4;

        // Sheet 1: Rider Master List
        const biSheet1 = riders.map(r => {
          const bm = getBranchMeta(brands, r as LegacyRiderRecord);
          const tenure = Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24));
          const transfers = r.history?.length ?? 0;
          return {
            fileNo: (r.fileNumber || 0).toString().padStart(pad, '0'),
            name: r.name, email: r.email ?? '—', phone: r.phone ?? '—', mobile: r.mobile,
            branchName: bm.branchName, branchCode: bm.branchCode, contract: r.contract,
            status: r.isActive ? 'Active' : 'Inactive',
            tenure, transfers,
            createdAt: new Date(r.createdAt).toLocaleDateString(),
            brandName: bm.brandName,
          };
        });

        // Sheet 2: Contract Breakdown
        const contractMap = new Map<string, { contract: string; total: number; active: number; inactive: number; branches: Set<string>; avgTenure: number }>();
        riders.forEach(r => {
          const bm = getBranchMeta(brands, r as LegacyRiderRecord);
          const e = contractMap.get(r.contract) ?? { contract: r.contract, total: 0, active: 0, inactive: 0, branches: new Set<string>(), avgTenure: 0 };
          e.total++; if (r.isActive) e.active++; else e.inactive++;
          e.branches.add(bm.compactLabel);
          contractMap.set(r.contract, e);
        });
        const biSheet2 = Array.from(contractMap.values()).map(c => {
          const contractRiders = riders.filter(r => r.contract === c.contract);
          const avgTenure = contractRiders.length > 0 ? Math.round(contractRiders.reduce((s, r) => s + Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)), 0) / contractRiders.length) : 0;
          return { ...c, branchList: Array.from(c.branches).join(', '), branchCount: c.branches.size, avgTenure, activeRate: c.total > 0 ? Math.round((c.active / c.total) * 100) : 0 };
        }).sort((a, b) => b.total - a.total);

        // Sheet 3: Branch Distribution
        const branchMap = new Map<string, { branchCode: string; branchName: string; total: number; active: number; inactive: number; contracts: Set<string>; avgTenure: number }>();
        riders.forEach(r => {
          const bm = getBranchMeta(brands, r as LegacyRiderRecord);
          const key = bm.branchCode;
          const e = branchMap.get(key) ?? { branchCode: bm.branchCode, branchName: bm.branchName, total: 0, active: 0, inactive: 0, contracts: new Set<string>(), avgTenure: 0 };
          e.total++; if (r.isActive) e.active++; else e.inactive++;
          e.contracts.add(r.contract);
          branchMap.set(key, e);
        });
        const biSheet3 = Array.from(branchMap.values()).map(h => {
          const brRiders = riders.filter(r => getBranchMeta(brands, r as LegacyRiderRecord).branchCode === h.branchCode);
          const avgTenure = brRiders.length > 0 ? Math.round(brRiders.reduce((s, r) => s + Math.ceil((Date.now() - r.createdAt) / (1000 * 60 * 60 * 24)), 0) / brRiders.length) : 0;
          return { ...h, contractList: Array.from(h.contracts).join(', '), contractCount: h.contracts.size, avgTenure, activeRate: h.total > 0 ? Math.round((h.active / h.total) * 100) : 0 };
        }).sort((a, b) => b.total - a.total);

        // Sheet 4: Transfer History
        const biSheet4 = riders.filter(r => r.history && r.history.length > 0).flatMap(r => {
          const rBm = getBranchMeta(brands, r as LegacyRiderRecord);
          return (r.history ?? []).map((h, idx) => {
            const hBm = getBranchMeta(brands, h.branchId);
            return {
              fileNo: (r.fileNumber || 0).toString().padStart(pad, '0'),
              riderName: r.name, contract: r.contract, currentBranch: rBm.branchName, currentCode: rBm.branchCode,
              transferNo: idx + 1, fromBranch: hBm.branchName, fromCode: hBm.branchCode,
              startDate: new Date(h.startDate).toLocaleDateString(),
              endDate: h.endDate ? new Date(h.endDate).toLocaleDateString() : 'Current',
              daysSpent: h.daysSpent ?? '—',
            };
          });
        });

        const downloadBIExport = () => {
          const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
          let tsv = 'RIDER MANAGER BI EXPORT — FULL DETAILS\n';
          tsv += `Generated: ${new Date().toLocaleString()} · ${riders.length} riders · ${totalContracts} contracts\n\n`;

          tsv += '=== SHEET 1: RIDER MASTER LIST ===\n';
          tsv += 'File No\tName\tEmail\tPhone\tMobile\tBranch\tBranch Code\tContract\tStatus\tTenure (Days)\tTransfers\tCreated At\tBrand\n';
          biSheet1.forEach(r => { tsv += `${r.fileNo}\t${r.name}\t${r.email}\t${r.phone}\t${r.mobile}\t${r.branchName}\t${r.branchCode}\t${r.contract}\t${r.status}\t${r.tenure}\t${r.transfers}\t${r.createdAt}\t${r.brandName}\n`; });

          tsv += '\n=== SHEET 2: CONTRACT BREAKDOWN ===\n';
          tsv += 'Contract\tTotal\tActive\tInactive\tActive Rate\tBranches\tBranch Names\tAvg Tenure (Days)\n';
          biSheet2.forEach(r => { tsv += `${r.contract}\t${r.total}\t${r.active}\t${r.inactive}\t${r.activeRate}%\t${r.branchCount}\t${r.branchList}\t${r.avgTenure}\n`; });

          tsv += '\n=== SHEET 3: BRANCH DISTRIBUTION ===\n';
          tsv += 'Branch Code\tBranch\tTotal\tActive\tInactive\tActive Rate\tContracts\tContract Names\tAvg Tenure (Days)\n';
          biSheet3.forEach(r => { tsv += `${r.branchCode}\t${r.branchName}\t${r.total}\t${r.active}\t${r.inactive}\t${r.activeRate}%\t${r.contractCount}\t${r.contractList}\t${r.avgTenure}\n`; });

          tsv += '\n=== SHEET 4: TRANSFER HISTORY ===\n';
          tsv += 'File No\tRider Name\tContract\tCurrent Branch\tCurrent Code\tTransfer #\tFrom Branch\tFrom Code\tStart Date\tEnd Date\tDays Spent\n';
          biSheet4.forEach(r => { tsv += `${r.fileNo}\t${r.riderName}\t${r.contract}\t${r.currentBranch}\t${r.currentCode}\t${r.transferNo}\t${r.fromBranch}\t${r.fromCode}\t${r.startDate}\t${r.endDate}\t${r.daysSpent}\n`; });

          const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `Rider_BI_Export_Full_${dateStr}.tsv`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
        };

        return (
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-blue-400" />
                <span className="text-sm font-bold text-zinc-100">BI Export — Full Details</span>
                <span className="text-[10px] text-zinc-500 ml-1">{riders.length} riders · {totalContracts} contracts · {biSheet4.length} transfers</span>
              </div>
              <button onClick={downloadBIExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors">
                <Download size={12} /> Download Full TSV
              </button>
            </div>

            {/* Sheet 1: Rider Master List */}
            <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 1 — Rider Master List</p>
                <span className="text-[10px] text-zinc-600">{biSheet1.length} rows</span>
              </div>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#252525] border-b border-[#333]">
                      {['File#','Name','Email','Phone','Mobile','Branch','Code','Contract','Status','Tenure','Xfers','Created'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {biSheet1.length === 0 ? (
                      <tr><td colSpan={12} className="px-4 py-8 text-center text-xs text-zinc-500">No riders available</td></tr>
                    ) : biSheet1.map((r, i) => (
                      <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                        <td className="px-2 py-1.5 text-[11px] font-mono font-bold text-zinc-200">{r.fileNo}</td>
                        <td className="px-2 py-1.5 text-[11px] font-semibold text-zinc-100 max-w-[100px] truncate">{r.name}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 max-w-[120px] truncate">{r.email}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400">{r.phone}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-300">{r.mobile}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 max-w-[80px] truncate">{r.branchName}</td>
                        <td className="px-2 py-1.5 text-[11px] font-bold text-zinc-300">{r.branchCode}</td>
                        <td className="px-2 py-1.5"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#2D2D2D] text-zinc-300">{r.contract}</span></td>
                        <td className="px-2 py-1.5"><span className={cn("text-[9px] font-bold px-1.5 py-0.5 rounded-full", r.status === 'Active' ? "bg-emerald-900/30 text-emerald-400 border border-emerald-800" : "bg-red-900/30 text-red-400 border border-red-800")}>{r.status}</span></td>
                        <td className="px-2 py-1.5"><span className={cn("text-[10px] font-bold px-1 py-0.5 rounded", r.tenure <= 30 ? "bg-blue-900/30 text-blue-400" : r.tenure <= 180 ? "bg-amber-900/30 text-amber-400" : "bg-emerald-900/30 text-emerald-400")}>{r.tenure}d</span></td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 text-center">{r.transfers}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-500 whitespace-nowrap">{r.createdAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sheet 2: Contract Breakdown */}
            <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 2 — Contract Breakdown</p>
                <span className="text-[10px] text-zinc-600">{biSheet2.length} contracts</span>
              </div>
              <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#252525] border-b border-[#333]">
                      {['Contract','Total','Active','Inactive','Rate','Branches','Branch Names','Avg Tenure'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {biSheet2.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-zinc-500">No contract data</td></tr>
                    ) : biSheet2.map((r, i) => (
                      <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded bg-indigo-600 text-white flex items-center justify-center text-[7px] font-bold shrink-0">{r.contract.slice(0, 2).toUpperCase()}</div>
                            <span className="text-[11px] font-semibold text-zinc-100">{r.contract}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-[11px] font-bold text-zinc-200 text-center">{r.total}</td>
                        <td className="px-2 py-1.5 text-[11px] text-emerald-400 text-center">{r.active}</td>
                        <td className="px-2 py-1.5 text-[11px] text-red-400 text-center">{r.inactive}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <div className="w-10 h-1.5 bg-[#2D2D2D] rounded-full overflow-hidden"><div className={cn("h-full rounded-full", r.activeRate >= 80 ? "bg-emerald-500" : r.activeRate >= 50 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${r.activeRate}%` }} /></div>
                            <span className="text-[9px] font-bold text-zinc-300">{r.activeRate}%</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-300 text-center">{r.branchCount}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 max-w-[120px] truncate">{r.branchList}</td>
                        <td className="px-2 py-1.5"><span className={cn("text-[10px] font-bold px-1 py-0.5 rounded", r.avgTenure <= 30 ? "bg-blue-900/30 text-blue-400" : r.avgTenure <= 180 ? "bg-amber-900/30 text-amber-400" : "bg-emerald-900/30 text-emerald-400")}>{r.avgTenure}d</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sheet 3: Hub Distribution */}
            <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 3 — Branch Distribution</p>
                <span className="text-[10px] text-zinc-600">{biSheet3.length} branches</span>
              </div>
              <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#252525] border-b border-[#333]">
                      {['Branch Code','Branch','Total','Active','Inactive','Rate','Contracts','Contract Names','Avg Tenure'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {biSheet3.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-xs text-zinc-500">No branch data</td></tr>
                    ) : biSheet3.map((r, i) => (
                      <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                        <td className="px-2 py-1.5 text-[11px] font-bold text-zinc-100">{r.branchCode}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-300 max-w-[100px] truncate">{r.branchName}</td>
                        <td className="px-2 py-1.5 text-[11px] font-bold text-zinc-200 text-center">{r.total}</td>
                        <td className="px-2 py-1.5 text-[11px] text-emerald-400 text-center">{r.active}</td>
                        <td className="px-2 py-1.5 text-[11px] text-red-400 text-center">{r.inactive}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <div className="w-10 h-1.5 bg-[#2D2D2D] rounded-full overflow-hidden"><div className={cn("h-full rounded-full", r.activeRate >= 80 ? "bg-emerald-500" : r.activeRate >= 50 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${r.activeRate}%` }} /></div>
                            <span className="text-[9px] font-bold text-zinc-300">{r.activeRate}%</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-300 text-center">{r.contractCount}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 max-w-[120px] truncate">{r.contractList}</td>
                        <td className="px-2 py-1.5"><span className={cn("text-[10px] font-bold px-1 py-0.5 rounded", r.avgTenure <= 30 ? "bg-blue-900/30 text-blue-400" : r.avgTenure <= 180 ? "bg-amber-900/30 text-amber-400" : "bg-emerald-900/30 text-emerald-400")}>{r.avgTenure}d</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sheet 4: Transfer History */}
            <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 4 — Transfer History</p>
                <span className="text-[10px] text-zinc-600">{biSheet4.length} records</span>
              </div>
              <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#252525] border-b border-[#333]">
                      {['File#','Rider','Contract','Cur Branch','Cur Code','Xfer#','From Branch','From Code','Start','End','Days'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {biSheet4.length === 0 ? (
                      <tr><td colSpan={11} className="px-4 py-8 text-center text-xs text-zinc-500">No transfer history records</td></tr>
                    ) : biSheet4.map((r, i) => (
                      <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                        <td className="px-2 py-1.5 text-[11px] font-mono font-bold text-zinc-200">{r.fileNo}</td>
                        <td className="px-2 py-1.5 text-[11px] font-semibold text-zinc-100 max-w-[90px] truncate">{r.riderName}</td>
                        <td className="px-2 py-1.5"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#2D2D2D] text-zinc-300">{r.contract}</span></td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-300">{r.currentBranch}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 max-w-[80px] truncate">{r.currentCode}</td>
                        <td className="px-2 py-1.5 text-[11px] font-bold text-blue-400 text-center">{r.transferNo}</td>
                        <td className="px-2 py-1.5 text-[11px] text-amber-400">{r.fromBranch}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-400 max-w-[80px] truncate">{r.fromCode}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-500 whitespace-nowrap">{r.startDate}</td>
                        <td className="px-2 py-1.5 text-[11px] text-zinc-500 whitespace-nowrap">{r.endDate}</td>
                        <td className="px-2 py-1.5"><span className={cn("text-[10px] font-bold px-1 py-0.5 rounded", typeof r.daysSpent === 'number' && r.daysSpent <= 30 ? "bg-blue-900/30 text-blue-400" : typeof r.daysSpent === 'number' && r.daysSpent <= 90 ? "bg-amber-900/30 text-amber-400" : typeof r.daysSpent === 'number' ? "bg-emerald-900/30 text-emerald-400" : "bg-[#2D2D2D] text-zinc-400")}>{r.daysSpent}{typeof r.daysSpent === 'number' ? 'd' : ''}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Privacy masking helpers — used when super admin views data without consent
// ─────────────────────────────────────────────────────────────────────────────

function maskName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[1][0]}***`;
  return `${name.substring(0, 2)}***`;
}

function maskMobile(mobile: string): string {
  const m = String(mobile ?? '');
  if (m.length <= 3) return '***';
  return m.slice(-3).padStart(m.length, '*');
}

function RidersList({ riders, settings, deleteRider, toggleRiderStatus, submitRequest, setSelectedRider, user, addRider, onEditRider, currentOrg, orgPlan, maskData, onDataExport }: { riders: Rider[], settings: any, deleteRider: any, toggleRiderStatus: any, submitRequest: any, setSelectedRider: any, user: any, addRider: any, onEditRider: any, currentOrg?: Organization | null, orgPlan?: OrgPlan | null, maskData?: boolean, onDataExport?: () => void }) {
  const isAdmin = hasTabVariant(user, 'rider-manager', 'database', 'admin');
  const isSA = isPlatformRole(user?.role);
  const canRestore = isAdmin || isSA;
  const [searchTerm, setSearchTerm] = usePersistentState('search');
  const [brandFilter, setBrandFilter] = useState('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [showTransferModal, setShowTransferModal] = useState<Rider | null>(null);
  const [targetBranchId, setTargetBranchId] = useState('');
  const [showRiderLimit, setShowRiderLimit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedRiders, setDeletedRiders] = useState<Rider[]>([]);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const contractFilter = searchParams.get('contract');
  const navigate = useNavigate();
  const toast = useToast();

  const loadDeletedRiders = async () => {
    try {
      const res = await apiFetch<{ data: any[] }>('/api/riders/deleted');
      setDeletedRiders((res.data ?? []).map((r: any) => ({
        id: r.id, name: r.name, email: r.email, phone: r.phone, mobile: r.mobile,
        branchId: r.branch_id, brandId: r.brand_id, fileNumber: r.file_number,
        contract: r.contract, isActive: !!r.is_active, createdAt: r.created_at,
        history: [], deletedAt: r.deleted_at,
      } as Rider & { deletedAt: number })));
    } catch { setDeletedRiders([]); }
  };

  const handleRestore = async (riderId: string) => {
    setRestoringId(riderId);
    try {
      await apiFetch(`/api/riders/${riderId}/restore`, { method: 'POST' });
      toast.success('Rider Restored', 'The rider has been restored to the active database.');
      await loadDeletedRiders();
    } catch (e: any) {
      toast.error('Restore Failed', e?.message ?? 'Unknown error');
    } finally { setRestoringId(null); }
  };

  useEffect(() => {
    getRepository().getBrands().then(all => setBrands(getRiderModuleBrands(all)));
  }, []);

  const handleTransferRequest = async () => {
    if (!showTransferModal || !targetBranchId) return;
    const riderName = showTransferModal.name;
    await submitRequest({
      type: RequestType.TRANSFER,
      branchId: targetBranchId,
      riderId: showTransferModal.id
    });
    setShowTransferModal(null);
    setTargetBranchId('');
    toast.success('Transfer Requested!', `Transfer request for "${riderName}" submitted successfully.`);
  };

  const getDaysInSystem = (createdAt: number) => {
    const diffTime = Math.abs(Date.now() - createdAt);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const exportToExcel = () => {
    onDataExport?.(); // fire audit log if impersonating
    const worksheet = XLSX.utils.json_to_sheet(riders.map(r => {
      const branchMeta = getBranchMeta(brands, r as LegacyRiderRecord);
      return {
        'File No': (r.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0'),
        'Name': maskData ? maskName(r.name) : r.name,
        'Email': r.email || '-',
        'Phone': r.phone || '-',
        'Mobile': maskData ? maskMobile(r.mobile) : r.mobile,
        'Brand': branchMeta.brandName,
        'Branch': branchMeta.branchName,
        'Branch Code': branchMeta.branchCode,
        'Contract': r.contract,
        'Created At': new Date(r.createdAt).toLocaleString()
      };
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Riders");
    XLSX.writeFile(workbook, "Riders_Data.xlsx");
  };

  const importFromExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const sheet = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]) as any[];
      for (const row of sheet) {
        const branchRef = row['Branch ID'] || row['Branch Code'] || row['Branch'] || row['Hub Code'] || '';
        const branchId = resolveBranchId(brands, String(branchRef));
        const branchMeta = getBranchMeta(brands, branchId);
        await addRider({
          fileNumber: row['File No'] ? parseInt(String(row['File No'])) : undefined,
          name: row['Name'] || '',
          email: row['Email'] && row['Email'] !== '-' ? row['Email'] : '',
          phone: row['Phone'] && row['Phone'] !== '-' ? row['Phone'] : '',
          mobile: row['Mobile'] || '',
          branchId,
          brandId: row['Brand ID'] || branchMeta.brand?.id,
          contract: row['Contract'] || ''
        });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const currentTransferBranch = showTransferModal ? getBranchMeta(brands, showTransferModal as LegacyRiderRecord) : null;
  const transferOptions = showTransferModal
    ? getScopedBranches(brands, user)
        .filter((branch) => branch.id !== currentTransferBranch?.branchId)
        .map((branch) => ({
          value: branch.id,
          label: `${getBrandByBranchId(brands, branch.id)?.name ?? 'Unknown brand'} — ${branch.name} (${branch.code})`,
        }))
    : [];

  const filteredRiders = riders.filter(r => 
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.fileNumber || '').toString().includes(searchTerm)
  ).sort((a, b) => a.fileNumber - b.fileNumber);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Riders Database</h2>
          <p className="text-sm text-slate-500 mt-1">Manage and export your rider information.</p>
        </div>
        <div className="flex gap-3">
          {showRiderLimit && <PlanLimitModal resource="Riders" currentPlan={orgPlan ?? null} onClose={() => setShowRiderLimit(false)} />}
          {user?.role !== UserRole.EMPLOYEE && (
            <button
              onClick={() => {
                if (!isSA && isAtPlanLimit(currentOrg ?? null, 'maxRiders', riders.length)) {
                  setShowRiderLimit(true);
                  return;
                }
                navigate(isAdmin ? '/apps/rider-manager/database/new' : '/apps/rider-manager/submit');
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm"
            >
              <UserPlus size={18} />
              Register New
            </button>
          )}
          {isAdmin && (
            <label className="flex items-center gap-2 px-5 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm font-medium hover:bg-zinc-50 transition-colors shadow-sm cursor-pointer">
              <Upload size={18} />
              Import Excel
              <input type="file" className="hidden" accept=".xlsx,.xls" onChange={importFromExcel} />
            </label>
          )}
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-medium hover:bg-zinc-800 transition-colors shadow-sm"
          >
            <Download size={18} />
            Export Excel
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
        <input
          type="text"
          placeholder="Search by name or file number..."
          className="w-full pl-12 pr-4 py-4 bg-white border border-zinc-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-zinc-900/5 transition-all text-lg"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {brands.length > 0 && (
        <div className="flex items-center gap-3">
          <CustomSelect
            value={brandFilter}
            onChange={v => setBrandFilter(v)}
            placeholder="All Brands"
            options={[{ value: '', label: 'All Brands' }, ...brands.map(b => ({ value: b.id, label: b.name }))]}
          />
          {canRestore && (
            <button
              onClick={() => { setShowDeleted(!showDeleted); if (!showDeleted) loadDeletedRiders(); }}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border transition-all',
                showDeleted
                  ? 'bg-red-50 text-red-600 border-red-200'
                  : 'bg-white text-zinc-400 border-zinc-200 hover:border-zinc-400'
              )}
            >
              <Trash2 size={13} />
              {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
              {showDeleted && deletedRiders.length > 0 && (
                <span className="text-[10px] font-mono bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{deletedRiders.length}</span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Deleted Riders Panel */}
      {showDeleted && canRestore && (
        <div className="bg-red-50/50 border border-red-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-red-100/60 border-b border-red-200 flex items-center gap-2">
            <Trash2 size={14} className="text-red-500" />
            <span className="text-xs font-bold text-red-700 uppercase tracking-widest">Deleted Riders</span>
            <span className="text-[10px] font-mono bg-red-200 text-red-700 px-1.5 py-0.5 rounded ml-1">{deletedRiders.length}</span>
          </div>
          {deletedRiders.length === 0 ? (
            <div className="p-8 text-center text-red-400 text-sm italic">No deleted riders found.</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-red-200">
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">File No</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Mobile</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Deleted</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {deletedRiders.map((r: any) => (
                  <tr key={r.id} className="hover:bg-red-100/40 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-zinc-500 text-sm">{(r.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0')}</td>
                    <td className="px-4 py-2.5 text-sm text-zinc-600">{maskData ? maskName(r.name) : r.name}</td>
                    <td className="px-4 py-2.5 text-sm text-zinc-400">{maskData ? maskMobile(r.mobile) : r.mobile}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-400">{r.deletedAt ? new Date(r.deletedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => handleRestore(r.id)}
                        disabled={restoringId === r.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-[11px] font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      >
                        <RotateCcw size={12} /> {restoringId === r.id ? 'Restoring...' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div id="riders-table" className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b border-slate-200">
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest">File No</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest">Name</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest">Mobile</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest">Branch / Code</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest">Brand</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest">Tenure</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(() => {
              const filtered = riders.filter(r => {
                const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase()) || (r.fileNumber || '').toString().includes(searchTerm);
                const matchesContract = !contractFilter || r.contract === contractFilter;
                const matchesBrand = !brandFilter || r.brandId === brandFilter;
                return matchesSearch && matchesContract && matchesBrand;
              }).sort((a, b) => a.fileNumber - b.fileNumber);

              return filtered.length > 0 ? filtered.map((rider) => {
                const branchMeta = getBranchMeta(brands, rider as LegacyRiderRecord);

                return (
                <tr key={rider.id} className={cn("hover:bg-slate-50 transition-colors group cursor-pointer even:bg-slate-50/40", !rider.isActive && "opacity-60")}>
                  <td
                    className="px-4 py-3 font-mono text-zinc-900 font-medium hover:text-emerald-600 transition-colors"
                    onClick={() => setSelectedRider(rider)}
                  >
                    {(rider.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0')}
                  </td>
                  <td
                    className="px-4 py-3"
                    onClick={() => setSelectedRider(rider)}
                  >
                    <div className={cn("font-semibold text-zinc-900 group-hover:text-emerald-600 transition-colors text-sm", !rider.isActive && "text-zinc-400")}>{maskData ? maskName(rider.name) : rider.name}</div>
                    <div className="text-xs text-zinc-400">{rider.contract}</div>
                  </td>
                  <td className="px-4 py-3">
                    <a
                      href={maskData ? '#' : `tel:${rider.mobile}`}
                      className="flex items-center gap-2 text-sm text-zinc-600 hover:text-emerald-600 transition-colors group/phone"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone size={14} className="text-zinc-400 group-hover/phone:text-emerald-500" />
                      {maskData ? maskMobile(rider.mobile) : rider.mobile}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-zinc-600">{branchMeta.branchName}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">{branchMeta.branchCode}</div>
                  </td>
                  <td className="px-4 py-3">
                    {branchMeta.brandName !== 'Unassigned brand'
                      ? <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-semibold border border-indigo-100">
                          {branchMeta.brandName}
                        </span>
                      : <span className="text-zinc-300">—</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-zinc-600">
                      <Clock size={14} className="text-zinc-400" />
                      {getDaysInSystem(rider.createdAt)} Days
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest",
                      rider.isActive
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                        : "bg-zinc-100 text-zinc-400 border border-zinc-200"
                    )}>
                      {rider.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setSelectedRider(rider)}
                        className="p-2 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                        title="View Credentials"
                      >
                        <Eye size={16} />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditRider(rider);
                            }}
                            className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({
                                message: 'Are you sure you want to delete this rider?',
                                onConfirm: () => deleteRider(rider.id),
                              });
                            }}
                            className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}) : (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center text-slate-400 italic">
                    {contractFilter ? `No riders found for contract: ${contractFilter}` : 'No riders found in the database.'}
                  </td>
                </tr>
              );
            })()}
          </tbody>
        </table>
        </div>
      </div>

      {showTransferModal && (
        <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-10 space-y-8">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold tracking-tight">Request Transfer</h3>
                <button onClick={() => setShowTransferModal(null)} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-1">Rider</p>
                  <p className="font-bold text-zinc-900">{showTransferModal.name}</p>
                  <p className="text-xs text-zinc-500">Current Branch: {currentTransferBranch?.fullLabel ?? 'Unassigned branch'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Target Branch</label>
                  <CustomSelect
                    value={targetBranchId}
                    onChange={v => setTargetBranchId(v)}
                    options={transferOptions}
                    placeholder="Select branch…"
                  />
                </div>
              </div>

              <button 
                onClick={handleTransferRequest}
                disabled={!targetBranchId}
                className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all flex items-center justify-center gap-2"
              >
                Send Transfer Request <ArrowRightLeft size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

const toTitleCase = (str: string) =>
  str.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

function RiderForm({ mode, nextAutoNumber, addRider, updateRider, initialData, settings, checkFileNumberConflict, onSave, toggleRiderStatus, user, onClose }: any) {
  const navigate = useNavigate();
  const [brands, setFormBrands] = useState<Brand[]>([]);
  const [formData, setFormData] = useState({
    fileNumber: initialData?.fileNumber?.toString() || '',
    name: initialData?.name || '',
    mobile: initialData?.mobile || '',
    contract: initialData?.contract || settings.contracts[0] || '',
    brandId: initialData?.brandId ?? '',
    branchId: initialData?.branchId ?? initialData?.hubCode ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [branchError, setBranchError] = useState<string | null>(null);
  const [localActive, setLocalActive] = useState<boolean>(initialData?.isActive ?? true);
  const riderFormSnap = React.useRef(mode === 'edit' ? JSON.parse(JSON.stringify({
    fileNumber: initialData?.fileNumber?.toString() || '',
    name: initialData?.name || '',
    mobile: initialData?.mobile || '',
    contract: initialData?.contract || settings.contracts[0] || '',
    brandId: initialData?.brandId ?? '',
    branchId: initialData?.branchId ?? initialData?.hubCode ?? '',
    isActive: initialData?.isActive ?? true,
  })) : null);
  const riderDirty = React.useMemo(() => mode === 'edit'
    ? hasDataChanged(riderFormSnap.current, { ...formData, isActive: localActive })
    : !!formData.name.trim(), [formData, localActive, mode]);

  useEffect(() => {
    getRepository().getBrands().then(all => {
      setFormBrands(getRiderModuleBrands(all));
    });
  }, []);

  useEffect(() => {
    if (formData.brandId || !formData.branchId) return;
    const brandId = getBranchMeta(brands, formData.branchId).brand?.id;
    if (brandId) {
      setFormData((prev) => ({ ...prev, brandId }));
    }
  }, [brands, formData.branchId, formData.brandId]);

  useEffect(() => {
    if (mode === 'add' && !formData.fileNumber) {
      setFormData(prev => ({ ...prev, fileNumber: (nextAutoNumber || '').toString() }));
    }
  }, [mode, nextAutoNumber]);

  const isModal = !!onClose;

  const handleFileNumberChange = (val: string) => {
    setFormData(prev => ({ ...prev, fileNumber: val }));
    if (!val) {
      setError(null);
      return;
    }
    const num = parseInt(val);
    if (!isNaN(num)) {
      const conflictName = checkFileNumberConflict(num, initialData?.id);
      if (conflictName) {
        setError(`Error: File Number [${num}] is already assigned to [${conflictName}].`);
      } else {
        setError(null);
      }
    } else {
      setError('Please enter a valid number');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (error) return;

    const branchId = resolveBranchId(brands, formData.branchId);
    const branchMeta = getBranchMeta(brands, branchId);
    if (!branchMeta.branch) {
      setBranchError('Select a branch before saving this rider.');
      return;
    }

    setBranchError(null);
    const riderData = {
      fileNumber: formData.fileNumber ? parseInt(formData.fileNumber) : nextAutoNumber,
      name: formData.name,
      mobile: formData.mobile,
      branchId: branchMeta.branch.id,
      contract: formData.contract,
      brandId: branchMeta.brand?.id,
    };

    if (mode === 'add') {
      const savedRider = await addRider(riderData);
      onSave(savedRider);
    } else {
      await updateRider({ ...initialData, ...riderData });
      if (onClose) { onClose(); } else { navigate('/apps/rider-manager/database'); }
    }
  };

  return (
    <div className={isModal ? "space-y-3" : "space-y-8"}>
      {!isModal && (
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {mode === 'add' ? 'Register New Rider' : 'Edit Rider Details'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {mode === 'add' ? 'Add a new member to the system.' : 'Update information for an existing member.'}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={isModal ? "space-y-3" : "bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4"}>
        <div className="space-y-1.5">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">File Number</label>
          <div className="relative">
            <input
              type="number"
              value={formData.fileNumber}
              onChange={(e) => handleFileNumberChange(e.target.value)}
              className={cn(
                "w-full font-mono bg-zinc-50 border focus:outline-none focus:border-zinc-900 focus:bg-white transition-all",
                isModal ? "px-3 py-2 rounded-lg text-sm" : "px-5 py-4 rounded-2xl text-lg",
                error ? "border-red-500 bg-red-50/30" : "border-zinc-200"
              )}
              placeholder={(nextAutoNumber || '').toString()}
            />
            {error && (
              <div className="flex items-center gap-1.5 mt-1 text-red-600 text-xs font-medium">
                <AlertCircle size={13} />{error}
              </div>
            )}
            {!error && !formData.fileNumber && (
              <div className="flex items-center gap-1.5 mt-1 text-zinc-400 text-[10px] font-medium">
                <AlertCircle size={12} />Will auto-assign: {nextAutoNumber}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Full Name</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => setFormData(prev => ({ ...prev, name: toTitleCase(e.target.value) }))}
            className={cn(
              "w-full bg-zinc-50 border border-zinc-200 focus:outline-none focus:border-zinc-900 focus:bg-white transition-all",
              isModal ? "px-3 py-2 rounded-lg text-sm" : "px-5 py-4 rounded-2xl text-lg"
            )}
            placeholder="John Doe"
          />
        </div>

        <div className={cn("grid grid-cols-2", isModal ? "gap-3" : "gap-6")}>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Mobile (Required)</label>
            <div className="relative">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
              <input
                type="tel"
                required
                value={formData.mobile}
                onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                className={cn(
                  "w-full pl-9 pr-3 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 transition-all",
                  isModal ? "py-2 text-sm" : "py-3"
                )}
                placeholder="e.g. 0123456789"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand</label>
            <CustomSelect
              value={formData.brandId}
              onChange={v => setFormData(prev => ({ ...prev, brandId: v, branchId: '' }))}
              options={[
                { value: '', label: 'All Rider Brands' },
                ...brands.map(b => ({ value: b.id, label: b.name })),
              ]}
              placeholder="Select brand…"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Branch</label>
            <CustomSelect
              value={formData.branchId}
              onChange={v => { setBranchError(null); setFormData(prev => ({ ...prev, branchId: v, brandId: prev.brandId || getBranchMeta(brands, v).brand?.id || '' })); }}
              options={(formData.brandId ? getBranchesForBrand(brands, formData.brandId) : flattenBranches(brands)).map((branch) => ({
                value: branch.id,
                label: `${getBrandByBranchId(brands, branch.id)?.name ?? 'Unknown brand'} — ${branch.name} (${branch.code})`,
              }))}
              placeholder="Select branch…"
            />
            {branchError && <p className="text-[10px] font-medium text-red-600">{branchError}</p>}
            {!branchError && (() => {
              const branchMeta = getBranchMeta(brands, formData.branchId);
              return branchMeta.branch ? <p className="text-[10px] text-emerald-600 font-medium">Branch: {branchMeta.compactLabel}</p> : null;
            })()}
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Contract</label>
            <CustomSelect
              value={formData.contract}
              onChange={v => setFormData(prev => ({ ...prev, contract: v }))}
              options={settings.contracts.map((c: string) => ({ value: c, label: c }))}
              placeholder="Select contract…"
            />
          </div>
        </div>

        {mode === 'edit' && toggleRiderStatus && (
          <div className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
            <div>
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Rider Status</label>
              <div className="flex items-center gap-2 mt-1">
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest",
                  localActive ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-zinc-100 text-zinc-400 border border-zinc-200"
                )}>{localActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { toggleRiderStatus(initialData.id, user); setLocalActive(prev => !prev); }}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-bold transition-all border",
                localActive
                  ? "bg-white text-zinc-700 border-zinc-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100"
              )}
            >{localActive ? 'Set Inactive' : 'Set Active'}</button>
          </div>
        )}

        <div className={cn("flex gap-3", isModal ? "pt-1" : "pt-4")}>
          <button
            type="button"
            onClick={() => onClose ? onClose() : navigate('/apps/rider-manager/database')}
            className={cn(
              "flex-1 border border-zinc-200 font-semibold hover:bg-zinc-50 transition-all",
              isModal ? "px-4 py-2 rounded-lg text-sm" : "px-6 py-4 rounded-2xl"
            )}
          >Cancel</button>
          {!riderDirty && mode === 'edit' && <span className="text-[10px] text-zinc-400 self-center">No changes detected</span>}
          <button
            type="submit"
            disabled={!!error || !riderDirty}
            className={cn(
              "flex-1 bg-zinc-900 text-white font-semibold hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-zinc-900/10",
              isModal ? "px-4 py-2 rounded-lg text-sm" : "px-6 py-4 rounded-2xl"
            )}
          >{mode === 'add' ? 'Register Rider' : 'Save Changes'}</button>
        </div>
      </form>
    </div>
  );
}

function RiderEditWrapper({ riders, updateRider, settings, checkFileNumberConflict, toggleRiderStatus, user }: any) {
  const { id } = useParams();
  const rider = riders.find((r: Rider) => r.id === id);
  if (!rider) return <div>Rider not found</div>;
  return <RiderForm mode="edit" initialData={rider} updateRider={updateRider} settings={settings} checkFileNumberConflict={checkFileNumberConflict} toggleRiderStatus={toggleRiderStatus} user={user} />;
}

export function RiderManagerModule({ user, riders, allRiders, requests, settings, deleteRider, toggleRiderStatus, submitRequest, updateRequestStatus, addRider, updateRider, checkFileNumberConflict, setShowDetailPopup, nextAutoNumber, updateSettings, currentOrg, orgPlan, maskData, onDataExport }: any) {
  const isAdmin = hasTabVariant(user, 'rider-manager', 'database', 'admin');
  const isSuperAdmin = isPlatformRole(user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const [editRider, setEditRider] = useState<Rider | null>(null);

  const pendingCount = requests.filter((r: any) => r.status === RequestStatus.PENDING).length;
  const myPendingCount = requests.filter((r: any) => r.status === RequestStatus.PENDING && r.requesterId === user?.id).length;

  const moduleTabs = [
    { key: 'dashboard', label: 'Dashboard',                             icon: <LayoutDashboard size={16} />, to: '/apps/rider-manager/dashboard' },
    { key: 'database',  label: isAdmin ? 'Database' : 'My Riders',     icon: <Users size={16} />,  to: '/apps/rider-manager/database' },
    { key: 'inbox',     label: 'Request Inbox',                        icon: <Inbox size={16} />,  to: '/apps/rider-manager/inbox',     badge: pendingCount },
    { key: 'submit',    label: 'New Request',                          icon: <UserPlus size={16} />, to: '/apps/rider-manager/submit' },
    { key: 'transfers', label: 'Transfer Requests',                    icon: <ArrowRightLeft size={16} />, to: '/apps/rider-manager/transfers', badge: myPendingCount },
    { key: 'analytics', label: isAdmin ? 'Analytics' : 'My Analytics', icon: <BarChart2 size={16} />, to: '/apps/rider-manager/analytics' },
    { key: 'settings',  label: 'App Settings',                        icon: <SettingsIcon size={16} />, to: '/apps/rider-manager/settings' },
  ];

  const isFormRoute = location.pathname.includes('/database/new') || location.pathname.includes('/database/edit/');

  return (
    <div className="space-y-4">
      {/* Tab Bar — hidden on form routes */}
      {!isFormRoute && (
        <ModuleTabs
          moduleId="rider-manager"
          tabs={moduleTabs}
          user={user}
          headerRight={
            <>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">
                {riders.length} riders
              </span>
              {pendingCount > 0 && (
                <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                  {pendingCount} pending
                </span>
              )}
            </>
          }
        />
      )}

      {/* Module Routes — each wrapped with TabGate for tab-level security */}
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<RiderDashboard riders={riders} settings={settings} user={user} />} />
        <Route path="database" element={<TabGate moduleId="rider-manager" tabId="database" user={user}><RidersList riders={riders} settings={settings} deleteRider={deleteRider} toggleRiderStatus={toggleRiderStatus} submitRequest={submitRequest} setSelectedRider={setShowDetailPopup} user={user} addRider={addRider} onEditRider={setEditRider} currentOrg={currentOrg} orgPlan={orgPlan} maskData={maskData} onDataExport={onDataExport} /></TabGate>} />
        <Route path="database/new" element={<TabGate moduleId="rider-manager" tabId="database" user={user}><ProtectedRoute permission={PERMISSIONS.RIDER_APPROVE_REQUESTS} user={user}><RiderForm mode="add" nextAutoNumber={nextAutoNumber} addRider={addRider} settings={settings} checkFileNumberConflict={checkFileNumberConflict} onSave={setShowDetailPopup} /></ProtectedRoute></TabGate>} />
        <Route path="database/edit/:id" element={<TabGate moduleId="rider-manager" tabId="database" user={user}><ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.DEPARTMENT_HEAD]} user={user}><RiderEditWrapper riders={allRiders} updateRider={updateRider} settings={settings} checkFileNumberConflict={checkFileNumberConflict} toggleRiderStatus={toggleRiderStatus} user={user} /></ProtectedRoute></TabGate>} />
        <Route path="inbox" element={<TabGate moduleId="rider-manager" tabId="inbox" user={user}><ProtectedRoute permission={PERMISSIONS.RIDER_APPROVE_REQUESTS} user={user}><RequestInbox requests={requests} updateRequestStatus={updateRequestStatus} addRider={addRider} updateRider={updateRider} settings={settings} setSelectedRider={setShowDetailPopup} riders={allRiders} user={user} /></ProtectedRoute></TabGate>} />
          <Route path="analytics" element={<TabGate moduleId="rider-manager" tabId="analytics" user={user}><GlobalAnalytics riders={riders} settings={settings} user={user} /></TabGate>} />
        <Route path="settings" element={<TabGate moduleId="rider-manager" tabId="settings" user={user}><ProtectedRoute permission={PERMISSIONS.RIDER_EDIT_SETTINGS} user={user}><RiderAppSettingsPage settings={settings} updateSettings={updateSettings} /></ProtectedRoute></TabGate>} />
        <Route path="submit" element={<TabGate moduleId="rider-manager" tabId="submit" user={user}><ProtectedRoute permission={PERMISSIONS.RIDER_SUBMIT_REQUEST} user={user}><SubmitRequestForm user={user} submitRequest={submitRequest} settings={settings} /></ProtectedRoute></TabGate>} />
        <Route path="transfers" element={<TabGate moduleId="rider-manager" tabId="transfers" user={user}><ProtectedRoute permission={PERMISSIONS.RIDER_SUBMIT_REQUEST} user={user}><ManagerTransfersPage requests={requests} user={user} riders={allRiders} setSelectedRider={setShowDetailPopup} /></ProtectedRoute></TabGate>} />
      </Routes>

      {editRider && (
        <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="bg-slate-900 px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Editing Rider</p>
                <p className="text-white font-bold text-sm mt-0.5">{editRider.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono text-slate-400">
                  #{(editRider.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0')}
                </span>
                <button
                  onClick={() => setEditRider(null)}
                  className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto max-h-[80vh]">
              <RiderForm
                mode="edit"
                initialData={editRider}
                updateRider={updateRider}
                settings={settings}
                checkFileNumberConflict={checkFileNumberConflict}
                toggleRiderStatus={toggleRiderStatus}
                user={user}
                onClose={() => setEditRider(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RiderAppSettingsPage({ settings, updateSettings }: { settings: any, updateSettings: any }) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const toast = useToast();

  useEffect(() => {
    getRepository().getBrands().then((all) => setBrands(getRiderModuleBrands(all)));
  }, []);

  const branches = flattenBranches(brands);

  const addContract = (val: string) => {
    if (!val) return;
    updateSettings({ ...settings, contracts: [...settings.contracts, val] });
  };

  const removeContract = (index: number) => {
    const newList = [...settings.contracts];
    newList.splice(index, 1);
    updateSettings({ ...settings, contracts: newList });
  };

  const exportSettings = () => {
    const data = {
      Config: [
        { Key: 'fileNoStart', Value: settings.fileNoStart },
        { Key: 'fileNoPadding', Value: settings.fileNoPadding },
        { Key: 'cardHeader', Value: settings.cardHeader ?? 'Shipsy Rider Account' }
      ],
      Contracts: settings.contracts.map((c: string) => ({ Name: c })),
    };
    const workbook = XLSX.utils.book_new();
    Object.entries(data).forEach(([name, rows]) => {
      const ws = XLSX.utils.json_to_sheet(rows as any[]);
      XLSX.utils.book_append_sheet(workbook, ws, name);
    });
    XLSX.writeFile(workbook, "RiderApp_Settings.xlsx");
  };

  const importSettings = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const newSettings = { ...settings };
      const configSheet = XLSX.utils.sheet_to_json(wb.Sheets['Config']) as any[];
      configSheet.forEach((row: any) => {
        if (row.Key === 'fileNoStart') newSettings.fileNoStart = parseInt(row.Value);
        if (row.Key === 'fileNoPadding') newSettings.fileNoPadding = parseInt(row.Value);
        if (row.Key === 'cardHeader') newSettings.cardHeader = String(row.Value);
      });
      if (wb.Sheets['Contracts']) newSettings.contracts = (XLSX.utils.sheet_to_json(wb.Sheets['Contracts']) as any[]).map((r: any) => r.Name);
      updateSettings(newSettings);
    };
    reader.readAsBinaryString(file);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">App Settings</h2>
          <p className="text-sm text-slate-500 mt-1">Review the brand and branch registry, numbering engine, card branding, and contracts.</p>
        </div>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 px-5 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm font-medium hover:bg-zinc-50 transition-colors shadow-sm cursor-pointer">
            <Upload size={18} />
            Import Excel
            <input type="file" className="hidden" accept=".xlsx,.xls" onChange={importSettings} />
          </label>
          <button
            onClick={exportSettings}
            className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-medium hover:bg-zinc-800 transition-colors shadow-sm"
          >
            <Download size={18} />
            Export Excel
          </button>
        </div>
      </div>

      {/* ── Branch Registry Summary — Managed Centrally ── */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-2">
          <SettingsIcon size={18} className="text-zinc-400" />
          <h3 className="font-bold text-zinc-900">Branch Registry</h3>
          <span className="ml-auto text-xs text-zinc-400">{branches.length} branch{branches.length !== 1 ? 'es' : ''}</span>
        </div>
        <p className="text-xs text-zinc-500">Branches now live in the shared Brand Registry. The Rider module reads those brand and branch assignments directly instead of maintaining a private location list.</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Enabled Brands</p>
            <p className="mt-1 text-xl font-bold text-zinc-900">{brands.length}</p>
          </div>
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Available Branches</p>
            <p className="mt-1 text-xl font-bold text-zinc-900">{branches.length}</p>
          </div>
        </div>

        <div className="space-y-2">
          {brands.length === 0 ? (
            <div className="py-6 text-center text-zinc-400 italic text-sm">No rider-enabled brands are configured yet.</div>
          ) : brands.map((brand) => (
            <div key={brand.id} className="rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-zinc-900">{brand.name}</p>
                  <p className="text-[11px] text-zinc-500">{(brand.branches ?? []).length} branch{(brand.branches ?? []).length !== 1 ? 'es' : ''}</p>
                </div>
                <div className="text-right text-[11px] text-zinc-500">
                  {(brand.branches ?? []).slice(0, 3).map((branch) => `${branch.name} (${branch.code})`).join(' · ') || 'No branches'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
          <h3 className="font-bold text-zinc-900 flex items-center gap-2">
            <FileText size={18} /> Numbering Engine
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Start Number</label>
              <input
                type="number"
                value={settings.fileNoStart}
                onChange={(e) => updateSettings({ ...settings, fileNoStart: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Padding Digits</label>
              <input
                type="number"
                value={settings.fileNoPadding}
                onChange={(e) => updateSettings({ ...settings, fileNoPadding: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 transition-all"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
          <h3 className="font-bold text-zinc-900 flex items-center gap-2">
            <ClipboardList size={18} /> Card Branding
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Credential Card Header</label>
              <input
                type="text"
                value={settings.cardHeader ?? 'Shipsy Rider Account'}
                onChange={(e) => updateSettings({ ...settings, cardHeader: e.target.value })}
                placeholder="e.g. Shipsy Rider Account"
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 transition-all"
              />
              <p className="text-[10px] text-zinc-400">This text appears as the blue header on the Credential Card.</p>
            </div>
            <div className="border border-zinc-200 rounded-xl overflow-hidden text-center">
              <div className="bg-[#89b4f7] py-2">
                <span className="text-sm font-bold text-black tracking-wide">
                  {settings.cardHeader || 'Shipsy Rider Account'}
                </span>
              </div>
              <div className="bg-zinc-50 py-1.5 text-[10px] text-zinc-400 italic">Live preview</div>
            </div>
          </div>
        </div>

        <ListManager title="Contracts" items={settings.contracts} onAdd={(v: string) => addContract(v)} onRemove={(i: number) => removeContract(i)} />
      </div>
    </div>
  );
}


function ManagerTransfersPage({ requests, user, riders, setSelectedRider }: { requests: WorkflowRequest[], user: User | null, riders: any[], setSelectedRider: any }) {
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    getRepository().getBrands().then((all) => setBrands(getRiderModuleBrands(all)));
  }, []);

  const myRequests = requests
    .filter((r: any) => r.requesterId === user?.id)
    .sort((a: any, b: any) => b.createdAt - a.createdAt);

  const pending = myRequests.filter((r: any) => r.status === RequestStatus.PENDING);
  const history = myRequests.filter((r: any) => r.status !== RequestStatus.PENDING);

  const getRiderName = (req: WorkflowRequest) => {
    if (req.riderId) {
      return riders.find((r: any) => r.id === req.riderId)?.name || 'Unknown Rider';
    }
    return req.riderData?.name || '—';
  };

  const getRequestBranchLabel = (req: WorkflowRequest) => getBranchMeta(brands, req.branchId).fullLabel;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Transfer Requests</h2>
        <p className="text-sm text-slate-500 mt-1">Track the status of requests you have submitted.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 p-4 rounded-xl shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600">
            <Clock size={20} />
          </div>
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-0.5">Pending</p>
            <p className="text-2xl font-bold tracking-tight">{pending.length}</p>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 p-4 rounded-xl shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
            <Check size={20} />
          </div>
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-0.5">Approved</p>
            <p className="text-2xl font-bold tracking-tight">{myRequests.filter((r: any) => r.status === RequestStatus.APPROVED).length}</p>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 p-4 rounded-xl shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-500">
            <X size={20} />
          </div>
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-0.5">Rejected</p>
            <p className="text-2xl font-bold tracking-tight">{myRequests.filter((r: any) => r.status === RequestStatus.REJECTED).length}</p>
          </div>
        </div>
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold tracking-tight flex items-center gap-2 text-amber-600">
            <Clock size={18} /> Awaiting Approval
          </h3>
          <div className="space-y-3">
            {pending.map((req: any) => (
              <div key={req.id} className="bg-amber-50 border border-amber-200 p-5 rounded-[1.5rem] flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-white", req.type === RequestType.NEW_RIDER ? "bg-emerald-500" : "bg-indigo-500")}>
                    {req.type === RequestType.NEW_RIDER ? <UserPlus size={18} /> : <ArrowRightLeft size={18} />}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">{req.type.replace('_', ' ')}</p>
                    <p className="font-bold text-zinc-900">{getRiderName(req)}</p>
                    <p className="text-xs text-zinc-500">Target Branch: <span className="font-bold">{getRequestBranchLabel(req)}</span></p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200">
                    Pending
                  </span>
                  <p className="text-[10px] text-zinc-400 mt-2">{new Date(req.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold tracking-tight flex items-center gap-2">
          <Inbox size={18} /> Request History
        </h3>
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
          {history.length === 0 ? (
            <div className="p-16 text-center text-slate-400 italic">No processed requests yet.</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b border-slate-200">
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Type</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rider</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Target Branch</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Admin Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {history.map((req: any) => (
                  <tr key={req.id} className="hover:bg-slate-50/50 transition-colors even:bg-slate-50/40">
                    <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums">{new Date(req.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-xs font-bold text-zinc-900">{req.type.replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      {req.riderId ? (
                        <button
                          onClick={() => {
                            const rider = riders.find((r: any) => r.id === req.riderId);
                            if (rider) setSelectedRider(rider);
                          }}
                          className="text-xs font-bold text-zinc-900 hover:underline"
                        >
                          {getRiderName(req)}
                        </button>
                      ) : (
                        <span className="text-xs font-bold text-zinc-900">{getRiderName(req)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{getRequestBranchLabel(req)}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest",
                        req.status === RequestStatus.APPROVED ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      )}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 italic">{req.adminNote || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


export function RiderDetailPopup({ rider, settings, onClose, user, submitRequest }: { rider: Rider, settings: any, onClose: () => void, user: User | null, submitRequest: any }) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showTransferSection, setShowTransferSection] = useState(false);
  const [targetBranchId, setTargetBranchId] = useState('');
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    getRepository().getBrands().then(all => setBrands(getRiderModuleBrands(all)));
  }, []);

  const isAdmin = hasTabVariant(user, 'rider-manager', 'database', 'admin');
  const firstName = rider.name.split(' ')[0].toLowerCase();
  const systemName = `${rider.name.split(' ')[0]} (${rider.contract})`;
  const username = `${firstName}${rider.fileNumber}`;
  const password = `${firstName.charAt(0)}${rider.fileNumber}`;
  const riderBranchMeta = getBranchMeta(brands, rider as LegacyRiderRecord);
  const transferOptions = getScopedBranches(brands, user)
    .filter((branch) => branch.id !== riderBranchMeta.branchId)
    .map((branch) => ({
      value: branch.id,
      label: `${getBrandByBranchId(brands, branch.id)?.name ?? 'Unknown brand'} — ${branch.name} (${branch.code})`,
    }));

  const handleClose = () => {
    setShowHistoryPanel(false);
    onClose();
  };

  const handleTransferRequest = async () => {
    if (!targetBranchId) return;
    await submitRequest({
      type: RequestType.TRANSFER,
      branchId: targetBranchId,
      riderId: rider.id
    });
    setShowTransferSection(false);
    handleClose();
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const downloadCard = async () => {
    const element = document.getElementById('credential-card');
    if (!element) return;
    try {
      const dataUrl = await domtoimage.toPng(element, { bgcolor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `Credential_${rider.fileNumber}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error downloading card:', error);
    }
  };

  const CopyButton = ({ text, field }: { text: string, field: string }) => (
    <button
      onClick={() => copyToClipboard(text, field)}
      className="p-1 hover:bg-zinc-100 rounded transition-colors text-zinc-400 hover:text-zinc-900"
      title="Copy to clipboard"
    >
      {copiedField === field ? <ClipboardCheck size={14} className="text-emerald-500" /> : <Copy size={14} />}
    </button>
  );

  // Career history (with fallback for riders without stored history)
  const historyEntries = getHistoryEntries(rider as LegacyRiderRecord, brands);

  const totalDays = historyEntries.reduce((total, entry) => {
    if (entry.endDate === null) return total + Math.max(1, Math.ceil((Date.now() - entry.startDate) / (1000 * 60 * 60 * 24)));
    return total + (entry.daysSpent ?? 0);
  }, 0);

  // Shared blue card component (uses dynamic header from settings)
  const ShipsyCard = () => (
    <div className="border border-zinc-200 rounded-lg overflow-hidden shadow-sm" id="credential-card">
      <div className="bg-[#89b4f7] py-3 text-center border-b border-zinc-800">
        <h4 className="text-2xl font-bold text-black tracking-wide">
          {settings.cardHeader || 'Shipsy Rider Account'}
        </h4>
      </div>
      <div className="grid grid-cols-4 bg-[#cccccc] border-b border-zinc-800">
        <div className="px-4 py-2 border-r border-zinc-800 text-center font-bold text-black uppercase text-[10px]">Branch</div>
        <div className="px-4 py-2 border-r border-zinc-800 text-center font-bold text-black uppercase text-[10px]">Name</div>
        <div className="px-4 py-2 border-r border-zinc-800 text-center font-bold text-black uppercase text-[10px]">USER</div>
        <div className="px-4 py-2 text-center font-bold text-black uppercase text-[10px]">PASS</div>
      </div>
      <div className="grid grid-cols-4 bg-white">
        <div className="px-4 py-3 border-r border-zinc-800 text-center text-black font-medium text-xs">{riderBranchMeta.compactLabel}</div>
        <div className="px-4 py-3 border-r border-zinc-800 text-center text-black font-medium text-xs">{systemName}</div>
        <div className="px-4 py-3 border-r border-zinc-800 text-center text-black font-medium text-xs">{username}</div>
        <div className="px-4 py-3 text-center text-black font-medium text-xs">{password}</div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Flex container: main modal + side-car sit side by side */}
      <div className="flex items-stretch max-h-[90vh] animate-in fade-in zoom-in duration-300">

        {/* ── Main Modal ── */}
        <div className={cn(
          "bg-white w-[36rem] shadow-2xl flex flex-col transition-all duration-300",
          showHistoryPanel ? "rounded-l-[2rem]" : "rounded-[2rem]"
        )}>
          <div className="p-8 space-y-6 overflow-y-auto flex-1">

            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold tracking-tight">Record Detail Bridge</h3>
              <div className="flex items-center gap-1">
                {/* History toggle — admin only */}
                <PermissionGate permission={PERMISSIONS.RIDER_VIEW_HISTORY} user={user}>
                  <div className="relative group">
                    <button
                      onClick={() => setShowHistoryPanel(prev => !prev)}
                      className={cn(
                        "p-2 rounded-full transition-all duration-200",
                        showHistoryPanel
                          ? "bg-zinc-900 text-white hover:bg-zinc-700"
                          : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900"
                      )}
                    >
                      <History size={20} />
                    </button>
                    {/* Tooltip */}
                    <div className="absolute right-0 top-full mt-2 px-3 py-1.5 bg-zinc-900 text-white text-xs font-medium rounded-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                      {showHistoryPanel ? 'Hide Career History' : 'View Career History'}
                    </div>
                  </div>
                </PermissionGate>
                <div className="relative group">
                  <button
                    onClick={() => navigate(`/apps/support-desk/ticket-center?prefill_type=rider&prefill_id=${rider.id}&prefill_label=${encodeURIComponent(`${rider.name} (#${rider.fileNumber})`)}&prefill_cat=RIDER_ISSUE`)}
                    className="p-2 text-zinc-400 hover:bg-amber-100 hover:text-amber-600 rounded-full transition-all duration-200"
                  >
                    <AlertCircle size={20} />
                  </button>
                  <div className="absolute right-0 top-full mt-2 px-3 py-1.5 bg-zinc-900 text-white text-xs font-medium rounded-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                    Report Issue
                  </div>
                </div>
                <button onClick={handleClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors ml-1">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Blue Shipsy Card — visible to everyone */}
            <ShipsyCard />

            <PermissionGate permission={PERMISSIONS.RIDER_VIEW_COPY_TOOLS} user={user}>
              <>
                {/* Admin-only: Copy Dashboard */}
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Copy Dashboard</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">File No</div>
                        <div className="font-semibold font-mono">{(rider.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0')}</div>
                      </div>
                      <CopyButton text={(rider.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0')} field="fileNo" />
                    </div>
                    <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">System Name</div>
                        <div className="font-semibold">{systemName}</div>
                      </div>
                      <CopyButton text={systemName} field="systemName" />
                    </div>
                    <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Username</div>
                        <div className="font-semibold">{username}</div>
                      </div>
                      <CopyButton text={username} field="username" />
                    </div>
                    <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Password</div>
                        <div className="font-semibold">{password}</div>
                      </div>
                      <CopyButton text={password} field="password" />
                    </div>
                    <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Branch</div>
                        <div className="font-semibold">{riderBranchMeta.fullLabel}</div>
                        <div className="text-[10px] text-zinc-400 font-medium">Code: {riderBranchMeta.branchCode}</div>
                      </div>
                      <CopyButton text={riderBranchMeta.fullLabel} field="branch" />
                    </div>
                    <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Mobile Number</div>
                        <div className="font-semibold">{rider.mobile}</div>
                      </div>
                      <CopyButton text={rider.mobile} field="mobile" />
                    </div>
                  </div>
                </div>

                {/* Admin: Download + Done */}
                <div className="flex gap-4 pt-2">
                  <button
                    onClick={downloadCard}
                    className="flex-1 py-4 bg-white border border-zinc-200 text-zinc-900 rounded-2xl font-bold hover:bg-zinc-50 transition-all flex items-center justify-center gap-2"
                  >
                    <Camera size={20} /> Download Card
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all flex items-center justify-center gap-2"
                  >
                    Done <ChevronRight size={20} />
                  </button>
                </div>
              </>
            </PermissionGate>

            {!hasPermFn(user, PERMISSIONS.RIDER_VIEW_COPY_TOOLS) && (
              <>
                {/* Store Manager: Transfer + Done only */}
                <div className="flex flex-col gap-4 pt-2">
                  {showTransferSection ? (
                    <div className="p-6 bg-zinc-50 rounded-2xl border border-zinc-200 animate-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-400">Request Transfer</h4>
                        <button onClick={() => setShowTransferSection(false)} className="text-zinc-400 hover:text-zinc-900"><X size={16} /></button>
                      </div>
                      <div className="flex gap-3">
                        <CustomSelect
                          value={targetBranchId}
                          onChange={v => setTargetBranchId(v)}
                          options={[{ value: '', label: 'Select Target Branch' }, ...transferOptions]}
                          placeholder="Select Target Branch"
                          className="flex-1"
                        />
                        <button
                          onClick={handleTransferRequest}
                          disabled={!targetBranchId}
                          className="px-6 py-2 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 disabled:opacity-50 transition-all"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowTransferSection(true)}
                      className="w-full py-3 bg-indigo-50 text-indigo-600 rounded-2xl font-bold hover:bg-indigo-100 transition-all flex items-center justify-center gap-2"
                    >
                      <ArrowRightLeft size={18} /> Request Transfer
                    </button>
                  )}
                  <button
                    onClick={handleClose}
                    className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all flex items-center justify-center gap-2"
                  >
                    Done <ChevronRight size={20} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Sliding Side-Car: Career Timeline (Admin Only) ── */}
        <PermissionGate permission={PERMISSIONS.RIDER_VIEW_HISTORY} user={user}>
          <div
            className={cn(
              "bg-white border-l border-zinc-100 shadow-2xl rounded-r-[2rem] flex-shrink-0 transition-all duration-300 overflow-hidden",
              showHistoryPanel ? "w-72 opacity-100" : "w-0 opacity-0 pointer-events-none"
            )}
          >
            {/* Fixed-width inner so content doesn't squish during transition */}
            <div className="w-72 h-full overflow-y-auto flex flex-col">

              {/* Panel header */}
              <div className="px-5 pt-5 pb-4 flex items-center justify-between border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <History size={14} className="text-zinc-400" />
                  <h4 className="text-[10px] font-bold text-zinc-900 uppercase tracking-widest">Career Timeline</h4>
                </div>
                <button
                  onClick={() => setShowHistoryPanel(false)}
                  className="p-1 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-400 hover:text-zinc-900"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Rider identity row */}
              <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/60">
                <p className="text-[10px] font-bold text-zinc-900 truncate">{rider.name}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">
                  {rider.contract} · #{(rider.fileNumber || 0).toString().padStart(settings.fileNoPadding || 0, '0')}
                </p>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-100">
                      <th className="px-4 py-2 text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Branch</th>
                      <th className="px-4 py-2 text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Period</th>
                      <th className="px-4 py-2 text-[9px] font-bold text-zinc-400 uppercase tracking-widest text-right">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-50">
                    {historyEntries.map((entry, idx) => {
                      const entryBranchMeta = getBranchMeta(brands, entry.branchId);
                      const isCurrent = entry.endDate === null;
                      const days = isCurrent
                        ? Math.max(1, Math.ceil((Date.now() - entry.startDate) / (1000 * 60 * 60 * 24)))
                        : entry.daysSpent ?? 0;
                      const startFmt = new Date(entry.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                      const endFmt = entry.endDate
                        ? new Date(entry.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                        : null;

                      return (
                        <tr key={idx} className={cn("transition-colors", isCurrent ? "bg-emerald-50/60" : "hover:bg-zinc-50/40")}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", isCurrent ? "bg-emerald-500" : "bg-zinc-300")} />
                              <div>
                                <p className={cn("text-[11px] font-bold leading-none", isCurrent ? "text-emerald-700" : "text-zinc-900")}>{entryBranchMeta.branchName}</p>
                                <p className="text-[9px] text-zinc-400 mt-0.5 leading-none">{entryBranchMeta.branchCode} · {entryBranchMeta.brandName}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-[10px] text-zinc-500 tabular-nums whitespace-nowrap">
                            {isCurrent ? (
                              <span>{startFmt} <span className="text-emerald-600 font-bold">→ Now</span></span>
                            ) : (
                              <span>{startFmt} → {endFmt}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={cn("text-[11px] font-bold tabular-nums", isCurrent ? "text-emerald-700" : "text-zinc-700")}>
                              {days}
                            </span>
                            <span className="text-[9px] text-zinc-400 ml-0.5">d</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer: total tenure */}
              <div className="border-t border-zinc-200 px-5 py-3 flex items-center justify-between bg-zinc-900">
                <div>
                  <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Total Tenure</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{historyEntries.length} branch{historyEntries.length !== 1 ? 'es' : ''}</p>
                </div>
                <p className="text-xl font-bold text-white tabular-nums">
                  {totalDays}<span className="text-xs font-normal text-zinc-400 ml-1">days</span>
                </p>
              </div>

            </div>
          </div>
        </PermissionGate>

      </div>
    </div>
  );
}

